/* SALES PLANNING — the monthly order & ex-factory program on the Portal (2026-10-20).

   One cycle at a time, in the order the work is done (Max, 2026-10-20):
     1 Order format  — Tina uploads the month's Master order format (SKUs and prices); a SKU can be added or corrected.
     2 Order sheets  — one sheet per buyer: its Sale OP PIC ticks the SKUs that buyer may order, sends the buyer a form
                       of exactly those, takes the filled form back in (or types it), and presses Done; Tina checks it.
     3 Stock         — Tina enters the three pools and allocates them in the buyers' priority.
     4 Production    — what is left to make; Production gives each SKU a factory and any overbuild.
   Overview is the tool's own grid (SKU × buyer) and its customer report, read-only. Setup holds the buyers (with their
   PIC). An order (a cycle) is CREATED by Tina on Order Format, with its format in the same step — none is seeded.

   Every rule — who may do what, the price column a buyer is valued at, which SKUs are not open this cycle — is the
   server's. The screen draws what it was told and multiplies; it never decides. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Download, Upload, Pencil, Save, Wand2, AlertTriangle, Plus, Trash2, Copy, Check, RotateCcw, Eye } from 'lucide-react';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import { BACKEND_URL } from './backendConfig';
import useRowWindow from './useRowWindow';
import { SPL_SCREENS } from './salesPlanningScreens';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import './SPHub.css';
import './ProjectApproval.css';
import './SalesPlanning.css';

const API = `${BACKEND_URL}/api/sales-planning`;
const call = async (url, method = 'GET', body) => {
  const r = await fetch(url, {
    method, credentials: 'include',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};
const readFile = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => rej(new Error('read')); r.readAsDataURL(f); });

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/* English dates carry the month's name (05/Dec/2026). */
const fmtD = (d) => { if (!d) return '—'; const [y, m, dd] = String(d).slice(0, 10).split('-'); return `${dd}/${MON[Number(m) - 1]}/${y}`; };
const fmtDT = (d) => { if (!d) return ''; const x = new Date(d); if (Number.isNaN(x.getTime())) return ''; return `${String(x.getDate()).padStart(2, '0')}/${MON[x.getMonth()]} ${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`; };
const n0 = (v) => (v ? Number(v).toLocaleString('en-US') : '');
const nz = (v) => Number(v || 0).toLocaleString('en-US');
const m2 = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd = (v) => `US$ ${m2(v)}`;
const POOLS = ['stock_kr_dom', 'stock_eusu', 'stock_kr_sop'];
const digits = (v) => String(v ?? '').replace(/[^\d]/g, '');
const numOf = (v) => { const n = Number(String(v ?? '').replace(/,/g, '')); return Number.isFinite(n) ? n : 0; };
const lineFig = (l) => { const order = l?.order_qty || 0; const stock = l?.from_stock || 0; return { order, stock, prod: order - Math.min(stock, order), over: stock > order }; };

function Modal({ title, onClose, onSave, saveLabel, busy, error, children, size, testid, t, saveDisabled, foot, footRight, cancelLabel, tag }) {
  return (
    <div className="spl-overlay" role="presentation">
      <div className={`spl-modal ${size || ''}`} role="dialog" aria-modal="true" aria-label={title} data-testid={testid}>
        <div className="spl-modal-head">
          <h3>{title}</h3>
          {tag}
          <button type="button" className="spl-modal-x" onClick={onClose} aria-label={t('spl.close')} title={t('spl.close')}><X size={18} /></button>
        </div>
        <div className="spl-modal-body">{children}</div>
        {error && <div className="pa-banner err spl-modal-err" data-testid="spl-modal-err">{error}</div>}
        {(onSave || foot || footRight) && (
          <div className="spl-modal-foot">
            {foot}
            <span className="spl-grow" />
            {footRight}
            <button type="button" className="pa-btn" onClick={onClose} data-testid="spl-modal-cancel">{cancelLabel || t('spl.cancel')}</button>
            {onSave && (
              <button type="button" className="pa-btn submit" onClick={onSave} disabled={busy || saveDisabled} data-testid="spl-modal-save">
                {busy ? <Loader2 size={14} className="pa-spin" /> : null} {saveLabel || t('spl.save')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* A table that draws only the rows on screen: fixed column widths (the table is their exact sum), a sticky head and an
   optional totals row under it. Every cell is one line, so every row is one height and the arithmetic is honest. */
function VTable({ rows, cols, rowKey, rowH = 32, testid, totals, resetKey, rowClass, empty, maxH, frozen = 0, tools }) {
  const [boxRef, win] = useRowWindow({ rowHeight: rowH, total: rows.length, min: 60, resetKey });
  const shown = win.windowed ? rows.slice(win.first, win.first + win.count) : rows;
  const width = cols.reduce((s, c) => s + c.w, 0);
  /* The first `frozen` columns hold still while the rest scrolls sideways; their offsets are the declared widths,
     which are exact because the table is table-layout: fixed at their sum. */
  const lefts = cols.map((c, i) => cols.slice(0, i).reduce((a, x) => a + x.w, 0));
  const fz = (i) => (i < frozen ? { cls: ` fz${i === frozen - 1 ? ' fz-last' : ''}`, st: { left: lefts[i] } } : { cls: '', st: undefined });
  return (
    <div className="spl-vbox" ref={boxRef} style={maxH ? { maxHeight: maxH } : undefined} data-testid={testid}>
      <table className="spl-vt" style={{ width }}>
        <colgroup>{cols.map((c) => <col key={c.key} style={{ width: c.w }} />)}</colgroup>
        <thead>
          {/* With `tools` (useColumnTools over the same columns), a column that has a `get` takes the portal's own
              Excel header — sort and a funnel of its values (Max, 2026-10-20, on the order format). `rows` is then
              the tools' own `shown`. */}
          <tr>{cols.map((c, i) => {
            const cls = `${c.num ? 'n' : ''}${fz(i).cls}${c.hcls ? ` ${c.hcls}` : ''}`;
            return tools && c.get
              ? <SortTh key={c.key} label={c.label} hint={c.tip || ''} className={cls} style={fz(i).st} {...tools.thProps(c.key)} />
              : <th key={c.key} className={cls} style={fz(i).st} title={c.tip || ''}>{c.label}</th>;
          })}</tr>
          {totals && <tr className="tot">{cols.map((c, i) => <th key={c.key} className={`${c.num ? 'n' : ''}${fz(i).cls}`} style={fz(i).st}>{totals[c.key] ?? ''}</th>)}</tr>}
        </thead>
        <tbody>
          {win.padTop > 0 && <tr style={{ height: win.padTop }}><td colSpan={cols.length} /></tr>}
          {shown.map((r) => (
            <tr key={rowKey(r)} style={{ height: rowH }} className={rowClass ? rowClass(r) : ''} data-key={rowKey(r)}>
              {cols.map((c, i) => <td key={c.key} className={[c.num ? 'n' : '', c.cls ? c.cls(r) : ''].join(' ') + fz(i).cls} style={fz(i).st} title={c.title ? c.title(r) : undefined}>{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
          {win.padBottom > 0 && <tr style={{ height: win.padBottom }}><td colSpan={cols.length} /></tr>}
          {!rows.length && <tr><td colSpan={cols.length} className="spl-empty">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------------------------ */
/* The working figures of one cycle, worked out once and shared by every tab.                                          */
function useModel(data) {
  return useMemo(() => {
    if (!data) return null;
    const custMap = new Map(data.customers.map((c) => [c.customer_key, c]));
    const buyers = data.customers.filter((c) => c.grid_order != null && c.status === 'active');
    const skuMap = new Map(data.skus.map((s) => [s.sap_code, s]));
    const lines = new Map();
    const bySku = new Map();
    const byCust = new Map();
    data.lines.forEach((l) => {
      lines.set(`${l.sap_code}|${l.customer_key}`, l);
      if (!bySku.has(l.sap_code)) bySku.set(l.sap_code, []);
      bySku.get(l.sap_code).push(l);
      if (!byCust.has(l.customer_key)) byCust.set(l.customer_key, []);
      byCust.get(l.customer_key).push(l);
    });
    const sheets = new Map(data.sheets.map((s) => [s.customer_key, s]));
    const priceOf = (sap, cust) => { const c = custMap.get(cust); const s = skuMap.get(sap); return c && s && c.price_field ? s[c.price_field] : null; };
    const skuFig = (sap) => {
      const ls = bySku.get(sap) || [];
      let order = 0; let stock = 0; let prod = 0; let over = 0;
      ls.forEach((l) => { const f = lineFig(l); order += f.order; stock += f.stock; prod += f.prod; if (f.over) over += 1; });
      const s = skuMap.get(sap) || {};
      const avail = POOLS.reduce((a, k) => a + (s[k] || 0), 0);
      return { order, stock, prod, over, avail, left: avail - stock, planned: prod + (s.overbuild || 0) };
    };
    const lineups = [...new Set(data.skus.map((s) => s.lineup).filter(Boolean))];
    const cats = [...new Set(data.skus.map((s) => s.line_category).filter(Boolean))];
    return { custMap, buyers, skuMap, lines, bySku, byCust, sheets, priceOf, skuFig, lineups, cats, notThisCycle: data.notThisCycle || {} };
  }, [data]);
}
const skuMatch = (s, q, lineup, cat) => {
  if (lineup && s.lineup !== lineup) return false;
  if (cat && s.line_category !== cat) return false;
  const n = q.trim().toLowerCase();
  return !n || [s.sap_code, s.product, s.skin_color, s.selling_sku].some((x) => String(x || '').toLowerCase().includes(n));
};
function SkuFilters({ t, model, q, setQ, lineup, setLineup, cat, setCat, testid }) {
  return (
    <>
      <input className="spl-search" placeholder={t('spl.search')} value={q} onChange={(e) => setQ(e.target.value)} data-testid={testid} />
      <select value={lineup} onChange={(e) => setLineup(e.target.value)} aria-label={t('spl.f_lineup')}>
        <option value="">{t('spl.allLineups')}</option>
        {model.lineups.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <select value={cat} onChange={(e) => setCat(e.target.value)} aria-label={t('spl.f_line_category')}>
        <option value="">{t('spl.allCategories')}</option>
        {model.cats.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
    </>
  );
}
const prodCell = (s) => (<>{s.product}{s.skin_color ? <span className="muted"> · {s.skin_color}</span> : null}</>);

/* ================================================================================================================== */
/* 1  ORDER FORMAT                                                                                                     */
/* The Master order format's own columns, in the file's own order (Max, 2026-10-20: "Format sẽ gồm có đủ các cột luôn
   nhé"). Product category is the Portal's — the file has none — and closes the row. */
const SKU_FIELDS = [['order_cycle', 'text'], ['noos', 'text'], ['lineup', 'text'], ['line_category', 'text'], ['selling_sku', 'text'],
  ['sap_code', 'text'], ['upc', 'text'], ['product', 'text', 'wide'], ['skin_color', 'text'], ['frame_color', 'text'], ['ex_factory_loc', 'text'],
  ['carton_qty', 'text'], ['hts_usa', 'text'], ['hts_eu', 'text'], ['ctn_size', 'text'], ['frame_category', 'text'],
  ['price_t1', 'num'], ['price_hema_dt', 'num'], ['price_t2', 'num'], ['product_category', 'text']];
const FORMAT_W = { order_cycle: 150, noos: 150, lineup: 110, line_category: 110, selling_sku: 92, sap_code: 92, upc: 118, product: 220,
  skin_color: 150, frame_color: 120, ex_factory_loc: 72, carton_qty: 70, hts_usa: 108, hts_eu: 108, ctn_size: 150, frame_category: 96,
  price_t1: 100, price_hema_dt: 92, price_t2: 100, product_category: 110 };
const PRICE_TINT = { price_t1: 'h-t1', price_hema_dt: 'h-hema', price_t2: 'h-t2' };
const usd$ = (v) => (v == null ? '–' : `$${m2(v)}`);

function FieldGrid({ t, fields, value, onChange, locked = [], options = {} }) {
  return (
    <div className="spl-fields">
      {fields.map(([k, type, wide]) => (
        <label key={k} className={wide ? 'wide' : ''}>{t(`spl.f_${k}`)}
          {options[k] ? (
            <select value={value[k] ?? ''} onChange={(e) => onChange(k, e.target.value)} disabled={locked.includes(k)} data-testid={`spl-f-${k}`}>
              <option value="">—</option>{options[k].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ) : type === 'date' && locked.includes(k) ? (
            /* A locked date reads as the rest of the portal prints one (01/Oct/2026), not as the browser's date box. */
            <input type="text" value={value[k] ? fmtD(value[k]) : ''} disabled data-testid={`spl-f-${k}`} />
          ) : (
            <input type={type === 'date' ? 'date' : 'text'} inputMode={type === 'num' ? 'decimal' : undefined} value={value[k] ?? ''} disabled={locked.includes(k)}
              onChange={(e) => onChange(k, e.target.value)} data-testid={`spl-f-${k}`} />
          )}
        </label>
      ))}
    </div>
  );
}

function FormatTab({ t, meta, data, model, setData, flash, readOnly = false }) {
  const [q, setQ] = useState('');
  const [lineup, setLineup] = useState('');
  const [cat, setCat] = useState('');
  const [upload, setUpload] = useState(false);
  const [edit, setEdit] = useState(null);       // { isNew, value }
  const [ask, setAsk] = useState(null);         // sku to remove
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const may = !readOnly && meta.can.manager && data.cycle.status !== 'closed';
  const rows = useMemo(() => data.skus.filter((s) => skuMatch(s, q, lineup, cat)), [data.skus, q, lineup, cat]);
  const prev = useMemo(() => [...meta.cycles].reverse().find((c) => c.skus > 0 && c.order_due < data.cycle.order_due), [meta.cycles, data.cycle.order_due]);

  const copy = async () => {
    setBusy(true); setErr('');
    try { const r = await call(`${API}/cycle/${data.cycle.cycle_pk}/format/copy`, 'POST', { from: prev.cycle_pk }); setData(r); flash(t('spl.fmtCopied', { n: r.copied })); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const saveSku = async () => {
    setBusy(true); setErr('');
    try {
      const v = edit.value;
      const r = edit.isNew
        ? await call(`${API}/cycle/${data.cycle.cycle_pk}/sku`, 'POST', v)
        : await call(`${API}/cycle/${data.cycle.cycle_pk}/sku/${encodeURIComponent(v.sap_code)}`, 'PUT', Object.fromEntries(SKU_FIELDS.filter(([k]) => k !== 'sap_code').map(([k]) => [k, v[k] ?? ''])));
      setData(r); setEdit(null); flash(t('spl.saved'));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setErr('');
    try { const r = await call(`${API}/cycle/${data.cycle.cycle_pk}/sku/${encodeURIComponent(ask.sap_code)}`, 'DELETE'); setData(r); setAsk(null); flash(t('spl.removed', { sap: ask.sap_code })); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!data.skus.length) {
    return (
      <section className="pa-section spl-emptybox" data-testid="spl-format-empty">
        <h3 className="spl-h">{t('spl.fmtEmpty')}</h3>
        <p className="spl-p">{t('spl.fmtEmptyHint')}</p>
        {err && <div className="pa-banner err">{err}</div>}
        {may ? (
          <div className="spl-row">
            <button type="button" className="pa-btn primary" onClick={() => setUpload(true)} data-testid="spl-format-upload"><Upload size={14} /> {t('spl.upload')}</button>
            {prev && <button type="button" className="pa-btn" onClick={copy} disabled={busy} data-testid="spl-format-copy"><Copy size={14} /> {t('spl.copyFrom', { cycle: fmtD(prev.order_due) })}</button>}
          </div>
        ) : <p className="spl-p muted">{t('spl.fmtWaiting')}</p>}
        {upload && <FormatUpload t={t} data={data} onClose={() => setUpload(false)} onDone={(r) => { setData(r); setUpload(false); flash(t('spl.fmtDone', { added: r.added.length, changed: r.changed.length, removed: r.removed })); }} />}
      </section>
    );
  }
  const cols = [
    { key: 'no', label: '#', w: 56, num: true, render: (s) => s.sort_order, get: (s) => s.sort_order },
    ...(may ? [{ key: 'act', label: '', w: 80, render: (s) => (
      <span className="spl-acts">
        <button type="button" className="spl-ibtn" title={t('spl.edit')} aria-label={`${t('spl.edit')} ${s.sap_code}`} onClick={() => { setErr(''); setEdit({ isNew: false, value: { ...s } }); }}><Pencil size={13} /></button>
        <button type="button" className="spl-ibtn red" title={t('spl.remove')} aria-label={`${t('spl.remove')} ${s.sap_code}`} onClick={() => { setErr(''); setAsk(s); }}><Trash2 size={13} /></button>
      </span>
    ) }] : []),
    ...SKU_FIELDS.map(([k, type]) => ({
      key: k, label: t(`spl.f_${k}`), w: FORMAT_W[k], num: type === 'num', hcls: PRICE_TINT[k] || (k === 'product_category' ? 'h-portal' : ''),
      get: type === 'num' ? (s) => (s[k] == null || s[k] === '' ? null : Number(s[k])) : (s) => s[k] ?? '',
      tip: k === 'product_category' ? t('spl.portalOnly') : t(`spl.f_${k}`),
      cls: () => (k === 'sap_code' || k === 'selling_sku' || k === 'upc' ? 'mono' : ''),
      title: (s) => (s[k] == null ? '' : String(s[k])),
      render: type === 'num' ? (s) => usd$(s[k])
        : k === 'order_cycle' ? (s) => (model.notThisCycle[s.sap_code] ? <span className="spl-chip warn">{s.order_cycle}</span> : s.order_cycle)
          : (s) => s[k] ?? '',
    })),
  ];
  return <FormatTable {...{ t, data, model, rows, cols, may, q, setQ, lineup, setLineup, cat, setCat, err, edit, ask, busy, setErr, setEdit, setAsk,
    upload, setUpload, setData, flash, saveSku, remove }} />;
}

/* The order format as a table — its own component so the column tools (a hook) sit beside the columns they read. */
function FormatTable({ t, data, model, rows, cols, may, q, setQ, lineup, setLineup, cat, setCat, err, edit, ask, busy, setErr, setEdit, setAsk,
  upload, setUpload, setData, flash, saveSku, remove }) {
  const tools = useColumnTools(rows, cols);
  return (
    <div>
      <div className="spl-toolbar">
        <SkuFilters t={t} model={model} q={q} setQ={setQ} lineup={lineup} setLineup={setLineup} cat={cat} setCat={setCat} testid="spl-format-search" />
        <span className="spl-line" data-testid="spl-format-count">{t('spl.fmtNote', { n: tools.shown.length, total: data.skus.length })}</span>
        {tools.active && <button type="button" className="pa-btn" onClick={tools.clear} data-testid="spl-format-clear">{t('spl.clearFilters')}</button>}
        <span className="spl-grow" />
        <a className="pa-btn" href={`${API}/cycle/${data.cycle.cycle_pk}/format.xlsx`} title={t('spl.fmtDownloadTip')} data-testid="spl-format-download"><Download size={14} /> {t('spl.fmtDownload')}</a>
        {may && <button type="button" className="pa-btn" onClick={() => { setErr(''); setEdit({ isNew: true, value: {} }); }} data-testid="spl-sku-add"><Plus size={14} /> {t('spl.addSku')}</button>}
        {may && <button type="button" className="pa-btn primary" onClick={() => setUpload(true)} data-testid="spl-format-upload"><Upload size={14} /> {t('spl.uploadNew')}</button>}
      </div>
      {err && !edit && !ask && <div className="pa-banner err">{err}</div>}
      <VTable rows={tools.shown} cols={cols} rowKey={(s) => s.sap_code} testid="spl-format-table" resetKey={`${q}|${lineup}|${cat}|${tools.shown.length}`}
        empty={t('spl.noRows')} frozen={may ? 2 : 1} tools={tools} />
      {upload && <FormatUpload t={t} data={data} onClose={() => setUpload(false)} onDone={(r) => { setData(r); setUpload(false); flash(t('spl.fmtDone', { added: r.added.length, changed: r.changed.length, removed: r.removed })); }} />}
      {edit && (
        <Modal t={t} size="wide" title={edit.isNew ? t('spl.skuNew') : t('spl.skuEdit', { sap: edit.value.sap_code })} onClose={() => setEdit(null)} onSave={saveSku} busy={busy} error={err} testid="spl-sku-dialog">
          <FieldGrid t={t} fields={SKU_FIELDS} value={edit.value} locked={edit.isNew ? [] : ['sap_code']} onChange={(k, v) => setEdit((e) => ({ ...e, value: { ...e.value, [k]: v } }))} />
        </Modal>
      )}
      {ask && (
        <Modal t={t} title={t('spl.remove')} onClose={() => setAsk(null)} onSave={remove} saveLabel={t('spl.remove')} busy={busy} error={err} testid="spl-sku-remove">
          <p className="spl-p">{t('spl.skuRemoveAsk', { sap: ask.sap_code, product: ask.product })}</p>
        </Modal>
      )}
    </div>
  );
}

function FormatUpload({ t, data, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [pv, setPv] = useState(null);
  const [rm, setRm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async (commit) => {
    setBusy(true); setErr('');
    try {
      const r = await call(`${API}/cycle/${data.cycle.cycle_pk}/format`, 'POST', { file: await readFile(file), fileName: file.name, commit, removeMissing: rm });
      if (commit) onDone(r); else setPv(r);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const free = pv ? pv.missing.filter((m) => !m.used).length : 0;
  const nothing = pv && !pv.added.length && !pv.changed.length && !(rm && free);
  return (
    <Modal t={t} size="wide" title={t('spl.fmtUploadTitle')} onClose={onClose} busy={busy} error={err} testid="spl-format-dialog"
      onSave={() => go(!!pv)} saveDisabled={!file || nothing} saveLabel={pv ? t('spl.write') : t('spl.read')}>
      <p className="spl-p muted">{t('spl.fmtUploadBody')}</p>
      <input type="file" accept=".xlsx,.xlsm" onChange={(e) => { setFile(e.target.files?.[0] || null); setPv(null); }} data-testid="spl-format-file" />
      {pv && (
        <div className="spl-preview" data-testid="spl-format-preview">
          <div className="spl-line">{t('spl.fmtSummary', { sheet: pv.sheet, rows: pv.rows, added: pv.added.length, changed: pv.changed.length, missing: pv.missing.length })}</div>
          {[...pv.duplicates, ...pv.warnings].slice(0, 8).map((w) => <div key={w} className="spl-warnline"><AlertTriangle size={12} /> {w}</div>)}
          {nothing && <div className="pa-banner ok">{t('spl.fmtNoChange')}</div>}
          {(pv.added.length > 0 || pv.changed.length > 0 || pv.missing.length > 0) && (
            <div className="spl-preview-box">
              <table className="pa-table">
                <tbody>
                  {pv.added.slice(0, 200).map((a) => <tr key={`a${a.sap}`}><td><span className="spl-chip ok">{t('spl.fmtAdded')}</span></td><td className="mono">{a.sap}</td><td>{a.product}{a.color ? <span className="muted"> · {a.color}</span> : null}</td></tr>)}
                  {pv.changed.slice(0, 200).map((c) => <tr key={`c${c.sap}`}><td><span className="spl-chip">{t('spl.fmtChanged')}</span></td><td className="mono">{c.sap}</td>
                    <td>{c.product} <span className="muted small">{c.diff.map((d) => `${t(`spl.f_${d.field}`)}: ${d.was ?? '—'} → ${d.now ?? '—'}`).join(' · ')}</span></td></tr>)}
                  {pv.missing.slice(0, 200).map((m) => <tr key={`m${m.sap}`}><td><span className="spl-chip warn">{t('spl.fmtMissing')}</span></td><td className="mono">{m.sap}</td>
                    <td>{m.product}{m.used ? <span className="muted small"> — {t('spl.fmtUsed')}</span> : null}</td></tr>)}
                </tbody>
              </table>
            </div>
          )}
          {free > 0 && <label className="spl-check"><input type="checkbox" checked={rm} onChange={(e) => setRm(e.target.checked)} /> {t('spl.fmtRemove', { n: free })}</label>}
        </div>
      )}
    </Modal>
  );
}

/* ================================================================================================================== */
/* 1  THE ORDERS — Tina creates each one (Max, 2026-10-20: "tôi muốn Tina sẽ là người tạo cái này luôn á … nên làm dạng
   bảng có nút tạo order rùi hiện popup rùi import vào"). A table of the orders made so far, Create order in a popup
   that takes the dates AND the order format in one go, and an order's format opened in a popup of its own. The
   schedule is no longer seeded: an order exists because somebody created it. */
/* What the order popup edits: its Status, and nothing else (Max, 2026-10-20, on the FC due, ETAs, items and remark copied
   from the schedule PDF: "thấy ko cần á chỉ cần tên file thui"). The order due and ex-factory dates are set when the
   order is created — they give its season and the SAP upload's delivery date — and the title carries them. */
const ORDER_FIELDS = [['status', 'sel']];
/* What an order covers, as the Monthly order program words it — offered, never imposed (the box takes any text). */
const seasonOf = (d) => { if (!d) return null; const [y, m] = String(d).slice(0, 10).split('-').map(Number); return m >= 10 ? y + 1 : y; };
const sLabel = (sn) => (sn ? `S${String(sn).slice(2)}` : '—');

function OrdersScreen({ t, meta, reloadMeta, data, model, setData, flash, openPk, openMode, setOpen }) {
  const [create, setCreate] = useState(false);
  const [del, setDel] = useState(null);        // the order to delete
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const may = meta.can.manager;
  const buyersN = meta.customers.filter((c) => c.grid_order != null && c.status === 'active').length;
  /* Newest order first: the one being worked on is at the top. */
  const rows = useMemo(() => [...meta.cycles].sort((a, b) => b.order_due.localeCompare(a.order_due) || b.ex_factory.localeCompare(a.ex_factory)), [meta.cycles]);
  const opened = meta.cycles.find((c) => c.cycle_pk === openPk);
  const dash = <span className="muted">—</span>;
  const columns = [
    { key: 'orderNo', label: t('spl.col_id'), get: (c) => c.order_no || '',
      cell: (c) => <button type="button" className="pa-link spl-orderlink mono" onClick={() => setOpen(c.cycle_pk, 'view')} data-testid="spl-order-id">{c.order_no || '—'}</button> },
    { key: 'splOrderDue', label: t('spl.f_order_due'), get: (c) => c.order_due, cell: (c) => fmtD(c.order_due) },
    { key: 'season', label: t('spl.f_season'), get: (c) => sLabel(c.season), cell: (c) => sLabel(c.season) },
    { key: 'splExFactory', label: t('spl.f_ex_factory'), get: (c) => c.ex_factory, cell: (c) => fmtD(c.ex_factory) },
    { key: 'file', label: t('spl.col_file'), get: (c) => c.format_file || '', td: 'small spl-filecell',
      cell: (c) => (c.format_file ? <span title={c.format_file}>{c.format_file}</span> : dash) },
    { key: 'skus', label: t('spl.col_format'), get: (c) => c.skus, td: 'num',
      cell: (c) => (c.skus ? nz(c.skus) : <span className="spl-chip warn">{t('spl.fmtNone')}</span>) },
    { key: 'sheets', label: t('spl.col_sheets'), get: (c) => c.sheets_done, td: 'num', cell: (c) => `${c.sheets_done}/${buyersN}` },
    { key: 'qty', label: t('spl.col_qty'), get: (c) => c.orders_total, td: 'num', cell: (c) => (c.orders_total ? nz(c.orders_total) : dash) },
    { key: 'status', label: t('spl.f_status'), get: (c) => t(`spl.st_${c.status}`),
      cell: (c) => <span className={`spl-chip ${c.status === 'closed' ? '' : 'ok'}`}>{t(`spl.st_${c.status}`)}</span> },
    { key: 'created', label: t('spl.col_createdBy'), get: (c) => c.created_by || '', cell: (c) => c.created_by || dash, td: 'small' },
    { key: 'splCreatedAt', label: t('spl.col_createdAt'), get: (c) => (c.created_at ? String(c.created_at).slice(0, 10) : ''),
      cell: (c) => (c.created_at ? fmtDT(c.created_at) : dash), td: 'small' },
  ];
  const tools = useColumnTools(rows, columns);

  const remove = async () => {
    setBusy(true); setErr('');
    try {
      await call(`${API}/cycles/${del.cycle_pk}`, 'DELETE');
      await reloadMeta(); setDel(null); flash(t('spl.orderDeleted', { date: fmtD(del.order_due) }));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="spl-toolbar">
        <span className="spl-line">{t('spl.ordersCount', { n: tools.shown.length, total: rows.length })}</span>
        {tools.active && <button type="button" className="pa-btn" onClick={tools.clear}>{t('spl.clearFilters')}</button>}
        <span className="spl-grow" />
        {may && <button type="button" className="pa-btn primary" onClick={() => setCreate(true)} data-testid="spl-order-create"><Plus size={14} /> {t('spl.newOrder')}</button>}
      </div>
      <div className="pa-table-wrap spl-orders-wrap">
        <table className="pa-table spl-orders" data-testid="spl-orders">
          <thead>
            <tr>
              {columns.map((c) => <SortTh key={c.key} label={c.label} {...tools.thProps(c.key)} />)}
              <th className="spl-orders-act">{t('spl.col_action')}</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length && <tr><td colSpan={columns.length + 1} className="spl-empty">{may ? t('spl.noOrderYetMgr') : t('spl.noOrderYet')}</td></tr>}
            {rows.length > 0 && !tools.shown.length && <tr><td colSpan={columns.length + 1} className="spl-empty">{t('spl.noRows')}</td></tr>}
            {tools.shown.map((c) => (
              <tr key={c.cycle_pk} data-pk={c.cycle_pk} className={`spl-orderrow${c.status === 'closed' ? ' spl-dim' : ''}${c.cycle_pk === openPk ? ' on' : ''}`}
                onClick={(e) => { if (!e.target.closest('button,a')) setOpen(c.cycle_pk, 'view'); }}>
                {columns.map((col) => <td key={col.key} className={col.td || ''}>{col.cell(c)}</td>)}
                <td className="spl-orders-act">
                  <span className="spl-acts">
                    <button type="button" className="spl-ibtn blue" onClick={() => setOpen(c.cycle_pk, 'view')} title={t('spl.viewOrder')} data-testid="spl-order-open">
                      <Eye size={13} /> {t('spl.view')}
                    </button>
                    {may && <button type="button" className="spl-ibtn" onClick={() => setOpen(c.cycle_pk, 'edit')} title={t('spl.editOrder')} aria-label={`${t('spl.edit')} ${c.order_no || fmtD(c.order_due)}`} data-testid="spl-order-edit"><Pencil size={13} /></button>}
                    {may && <button type="button" className="spl-ibtn red" onClick={() => { setErr(''); setDel(c); }} title={t('spl.delOrder')} aria-label={`${t('spl.delOrder')} ${fmtD(c.order_due)}`} data-testid="spl-order-delete"><Trash2 size={13} /></button>}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {create && <CreateOrder t={t} meta={meta} onClose={() => setCreate(false)} onDone={async (r) => {
        setCreate(false); await reloadMeta();
        const n = r.format ? r.format.rows : r.copy ? r.copy.skus : 0;
        flash(n ? t('spl.orderCreated', { date: fmtD(r.order_due), n }) : t('spl.orderCreatedNoFmt', { date: fmtD(r.order_due) }));
      }} />}
      {del && (
        <Modal t={t} title={t('spl.delOrder')} onClose={() => setDel(null)} onSave={remove} saveLabel={t('spl.delOrder')} busy={busy} error={err} testid="spl-order-delete-dialog">
          <p className="spl-p">{t('spl.delAsk', { date: fmtD(del.order_due), exf: fmtD(del.ex_factory), n: del.skus })}</p>
        </Modal>
      )}
      {opened && (
        <OrderDialog key={`${opened.cycle_pk}|${openMode}`} t={t} meta={meta} reloadMeta={reloadMeta} order={opened}
          mode={may && openMode === 'edit' ? 'edit' : 'view'} setMode={(m) => setOpen(opened.cycle_pk, m)}
          data={data} model={model} setData={setData} flash={flash} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}

/* ONE POPUP FOR AN ORDER, IN TWO MODES (Max, 2026-10-20: "các view và cái edit đang khác popup nhau nên cần combine lại.
   View là chỉ xem còn edit là được sửa"). View and Edit were two different popups — the format in one, the dates in
   another — so the order was never seen whole. Now one popup carries both: the order's own fields on top, its format
   under them. VIEW locks everything (the fields read, no Add / Edit / Remove / Upload on the format — Download stays,
   it changes nothing); EDIT opens the fields with one Save, and the format's own tools act as they always have. A
   viewer with the right sees "Edit" in the foot and switches in place. */
function OrderDialog({ t, meta, reloadMeta, order, mode, setMode, data, model, setData, flash, onClose }) {
  const editing = mode === 'edit';
  const [form, setForm] = useState(() => ({ ...order }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const dirty = ORDER_FIELDS.filter(([k]) => String(form[k] ?? '') !== String(order[k] ?? '')).length;
  const save = async () => {
    setBusy(true); setErr('');
    try {
      await call(`${API}/cycles/${order.cycle_pk}`, 'PUT', Object.fromEntries(ORDER_FIELDS.map(([k]) => [k, form[k] ?? ''])));
      await reloadMeta(); flash(t('spl.saved')); setMode('view');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const ready = data && model && data.cycle.cycle_pk === order.cycle_pk;
  return (
    <Modal t={t} size="sheet" testid="spl-order-dialog"
      title={`${order.order_no || ''} · ${t('spl.formatOf', { date: fmtD(order.order_due), season: sLabel(order.season), exf: fmtD(order.ex_factory) })}`}
      tag={<span className={`spl-chip ${editing ? 'warn' : ''}`} data-testid="spl-order-mode">{editing ? t('spl.modeEdit') : t('spl.modeView')}</span>}
      onClose={onClose} error={err} busy={busy}
      cancelLabel={editing ? t('spl.cancel') : t('spl.close')}
      onSave={editing ? save : undefined} saveDisabled={!dirty} saveLabel={dirty ? t('spl.saveN', { n: dirty }) : t('spl.save')}
      footRight={!editing && meta.can.manager
        ? <button type="button" className="pa-btn" onClick={() => setMode('edit')} data-testid="spl-order-to-edit"><Pencil size={14} /> {t('spl.edit')}</button>
        : <span />}>
      {/* THE FILE, NOT THE SCHEDULE (2026-10-20): which order format file this order is built on, who loaded it and
          when — the one fact about the order the title does not already carry — and its status. */}
      <div className="spl-orderhead spl-orderfile" data-testid="spl-order-fields">
        <div className="spl-filebox" data-testid="spl-order-file">
          <span className="spl-filelabel">{t('spl.col_file')}</span>
          {order.format_file
            ? <><b title={order.format_file}>{order.format_file}</b>
              {order.format_by && <span className="muted small">{t('spl.fileBy', { by: order.format_by, at: order.format_at ? fmtDT(order.format_at) : '' })}</span>}</>
            : <span className="muted">{t('spl.fileNone')}</span>}
        </div>
        <label className="spl-statusbox">{t('spl.f_status')}
          {editing
            ? <select value={form.status || 'open'} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="spl-f-status">
              <option value="open">{t('spl.st_open')}</option>
              <option value="closed">{t('spl.st_closed')}</option>
            </select>
            : <span data-testid="spl-f-status" className={`spl-chip ${order.status === 'closed' ? '' : 'ok'}`}>{t(`spl.st_${order.status || 'open'}`)}</span>}
        </label>
      </div>
      <h4 className="spl-subh">{t('spl.formatHead', { n: order.skus || 0 })}</h4>
      {ready
        ? <FormatTab t={t} meta={meta} data={data} model={model} setData={setData} flash={flash} readOnly={!editing} />
        : <div className="spl-line"><Loader2 size={14} className="pa-spin" /> {t('spl.loading')}</div>}
    </Modal>
  );
}

/* Create order: the dates, what it covers, and its order format — the Master file read on the spot (its sheet and
   SKU count shown before anything is written), an earlier order's format copied, or none yet. One press writes it all. */
function CreateOrder({ t, meta, onClose, onDone }) {
  const [v, setV] = useState({ order_due: '', ex_factory: '' });
  const withFmt = meta.cycles.filter((c) => c.skus > 0).sort((a, b) => b.order_due.localeCompare(a.order_due));
  const [src, setSrc] = useState('file');
  const [file, setFile] = useState(null);
  const [from, setFrom] = useState(withFmt[0] ? String(withFmt[0].cycle_pk) : '');
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fmtPart = async () => (src === 'file' && file ? { file: await readFile(file), fileName: file.name }
    : src === 'copy' && from ? { copyFrom: Number(from) } : {});
  /* The file is read as soon as there is one and both dates, so what will be written is on screen before the press.
     Only the dates and the source re-read it — typing the items or a remark does not. */
  const datesSet = !!(v.order_due && v.ex_factory);
  useEffect(() => {
    let live = true;
    setPv(null);
    if (!datesSet || src === 'later' || (src === 'file' && !file) || (src === 'copy' && !from)) return undefined;
    (async () => {
      setBusy(true); setErr('');
      try {
        const r = await call(`${API}/cycles`, 'POST', { order_due: v.order_due, ex_factory: v.ex_factory, commit: false, ...(await fmtPart()) });
        if (live) setPv(r);
      } catch (e) { if (live) setErr(e.message); } finally { if (live) setBusy(false); }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.order_due, v.ex_factory, src, file, from]);
  const go = async () => {
    setBusy(true); setErr('');
    try { const r = await call(`${API}/cycles`, 'POST', { ...v, commit: true, ...(await fmtPart()) }); onDone({ ...r, order_due: v.order_due }); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const ready = datesSet && (src === 'later' || (src === 'file' && file && pv) || (src === 'copy' && from && pv));
  const set = (k, x) => setV((o) => ({ ...o, [k]: x }));
  return (
    <Modal t={t} size="wide" title={t('spl.newOrderTitle')} onClose={onClose} onSave={go} saveLabel={t('spl.newOrder')} busy={busy} error={err}
      saveDisabled={!ready} testid="spl-order-create-dialog">
      <div className="spl-fields">
        <label>{t('spl.f_order_due')} *<input type="date" value={v.order_due} onChange={(e) => set('order_due', e.target.value)} data-testid="spl-new-order_due" /></label>
        <label>{t('spl.f_ex_factory')} *<input type="date" value={v.ex_factory} onChange={(e) => set('ex_factory', e.target.value)} data-testid="spl-new-ex_factory" /></label>
        <label>{t('spl.f_season')}<input type="text" value={v.order_due ? sLabel(seasonOf(v.order_due)) : ''} disabled title={t('spl.seasonAutoTip')} /></label>
      </div>
      <div className="spl-srcbox" data-testid="spl-new-src">
        <div className="spl-h small">{t('spl.fmtSource')}</div>
        <label className="spl-check"><input type="radio" name="spl-src" checked={src === 'file'} onChange={() => setSrc('file')} /> {t('spl.src_file')}</label>
        {src === 'file' && (
          <div className="spl-srcin">
            <input type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="spl-new-file" />
          </div>
        )}
        <label className="spl-check"><input type="radio" name="spl-src" checked={src === 'copy'} onChange={() => setSrc('copy')} disabled={!withFmt.length} /> {t('spl.src_copy')}</label>
        {src === 'copy' && (
          <div className="spl-srcin">
            <select value={from} onChange={(e) => setFrom(e.target.value)} data-testid="spl-new-from">
              {withFmt.map((c) => <option key={c.cycle_pk} value={c.cycle_pk}>{c.order_no ? `${c.order_no} · ` : ''}{t('spl.copyOption', { date: fmtD(c.order_due), exf: fmtD(c.ex_factory), n: nz(c.skus) })}</option>)}
            </select>
          </div>
        )}
        <label className="spl-check"><input type="radio" name="spl-src" checked={src === 'later'} onChange={() => setSrc('later')} /> {t('spl.src_later')}</label>
      </div>
      {!datesSet && src !== 'later' && <p className="spl-p muted small">{t('spl.datesFirst')}</p>}
      {pv && (
        <div className="spl-preview" data-testid="spl-new-preview">
          {pv.format && <div className="pa-banner ok">{t('spl.pvFile', { sheet: pv.format.sheet, rows: nz(pv.format.rows) })}</div>}
          {pv.copy && <div className="pa-banner ok">{t('spl.pvCopy', { n: nz(pv.copy.skus), date: fmtD(pv.copy.order_due) })}</div>}
          {pv.format && [...pv.format.duplicates, ...pv.format.warnings].slice(0, 8).map((w) => <div key={w} className="spl-warnline"><AlertTriangle size={12} /> {w}</div>)}
        </div>
      )}
    </Modal>
  );
}

/* ================================================================================================================== */
/* 2  ORDER SHEETS                                                                                                     */
const sheetState = (sheet, lines) => (sheet?.status && sheet.status !== 'open' ? sheet.status : (sheet || lines?.length ? 'open' : 'none'));
/* Who may do what with a sheet — the server's rule (sheetRights), drawn here so a dead button can say why. */
const sheetCan = (meta, cycle, cust, sheet) => {
  const status = sheet?.status || 'open';
  const closed = cycle.status === 'closed';
  const mine = !cust.pic_user_id || cust.pic_user_id === meta.me.uid;
  const staff = meta.can.edit && mine;
  return {
    edit: !closed && (meta.can.manager || (staff && status === 'open')),
    done: !closed && status === 'open' && (meta.can.manager || staff),
    reopen: !closed && ((meta.can.manager && status !== 'open') || (staff && status === 'done')),
    check: !closed && meta.can.manager && status === 'done',
    mine,
  };
};

function SheetsTab({ t, meta, data, model, setData, flash, openKey, setOpenKey }) {
  const [mine, setMine] = useState(false);
  const [status, setStatus] = useState('');
  const rows = useMemo(() => model.buyers.map((c) => {
    const ls = model.byCust.get(c.customer_key) || [];
    let qty = 0; let amount = 0; let ordered = 0; let ticked = 0;
    ls.forEach((l) => {
      if (l.offered) ticked += 1;
      if (l.order_qty) { ordered += 1; qty += l.order_qty; amount += l.order_qty * (model.priceOf(l.sap_code, c.customer_key) || 0); }
    });
    const sheet = model.sheets.get(c.customer_key);
    return { c, sheet, st: sheetState(sheet, ls), qty, amount, ordered, ticked };
  }).filter((r) => (!mine || r.c.pic_user_id === meta.me.uid) && (!status || r.st === status)), [model, mine, status, meta.me.uid]);
  const done = model.buyers.filter((c) => ['done', 'checked'].includes(model.sheets.get(c.customer_key)?.status)).length;
  const open = openKey ? model.custMap.get(openKey) : null;
  if (!data.skus.length) return <section className="pa-section spl-emptybox"><p className="spl-p">{t('spl.noFormatYet')}</p></section>;
  const cols = [
    { key: 'buyer', label: t('spl.buyer'), w: 190, render: (r) => <button type="button" className="spl-link" onClick={() => setOpenKey(r.c.customer_key)} data-testid="spl-sheet-open">{r.c.customer_key}</button> },
    { key: 'pic', label: t('spl.pic'), w: 150, render: (r) => r.c.pic_name || <span className="muted">—</span> },
    { key: 'price', label: t('spl.priceCol'), w: 120, render: (r) => t(`spl.pf_${r.c.price_field}`) },
    { key: 'status', label: t('spl.status'), w: 120, render: (r) => <span className={`spl-chip st-${r.st}`}>{t(`spl.sh_${r.st}`)}</span> },
    { key: 'ticked', label: t('spl.ticked'), w: 90, num: true, render: (r) => n0(r.ticked) },
    { key: 'ordered', label: t('spl.ordered'), w: 100, num: true, render: (r) => n0(r.ordered) },
    { key: 'qty', label: t('spl.qty'), w: 100, num: true, render: (r) => n0(r.qty) },
    { key: 'amount', label: t('spl.amountUsd'), w: 130, num: true, render: (r) => (r.amount ? m2(r.amount) : '') },
    { key: 'updated', label: t('spl.updated'), w: 190, render: (r) => (r.sheet?.updated_at ? <span className="small">{r.sheet.updated_by} · {fmtDT(r.sheet.updated_at)}</span> : '') },
    { key: 'sap', label: '', w: 90, render: (r) => (r.qty ? <a className="spl-ibtn wide" href={`${API}/cycle/${data.cycle.cycle_pk}/sap-upload.xlsx?customer=${encodeURIComponent(r.c.customer_key)}`} title={t('spl.sapOne')}><Download size={12} /> SAP</a> : null) },
  ];
  return (
    <div>
      <div className="spl-toolbar">
        <label className="spl-check"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="spl-mine" /> {t('spl.myBuyers')}</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('spl.status')}>
          <option value="">{t('spl.allStatus')}</option>
          {['none', 'open', 'done', 'checked'].map((s) => <option key={s} value={s}>{t(`spl.sh_${s}`)}</option>)}
        </select>
        <span className="spl-line">{t('spl.sheetsNote', { done, n: model.buyers.length })}</span>
        <span className="spl-grow" />
        <a className="pa-btn" href={`${API}/cycle/${data.cycle.cycle_pk}/sap-upload.xlsx`} data-testid="spl-sap-all"><Download size={14} /> {t('spl.sapAll')}</a>
      </div>
      <VTable rows={rows} cols={cols} rowKey={(r) => r.c.customer_key} testid="spl-sheets-table" resetKey={`${mine}|${status}`} rowH={36} empty={t('spl.noRows')} />
      {open && <SheetModal key={open.customer_key} t={t} meta={meta} data={data} model={model} cust={open} setData={setData} flash={flash} onClose={() => setOpenKey(null)} />}
    </div>
  );
}

function SheetModal({ t, meta, data, model, cust, setData, flash, onClose }) {
  const sheet = model.sheets.get(cust.customer_key);
  const can = sheetCan(meta, data.cycle, cust, sheet);
  const mineLines = model.byCust.get(cust.customer_key) || [];
  const anyTicked = mineLines.some((l) => l.offered);
  const [show, setShow] = useState(anyTicked ? 'ticked' : 'all');
  const [q, setQ] = useState('');
  const [lineup, setLineup] = useState('');
  const [cat, setCat] = useState('');
  const [draft, setDraft] = useState({});          // sap → { offered?, order_qty? }
  const [po, setPo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [leaving, setLeaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const line = (sap) => model.lines.get(`${sap}|${cust.customer_key}`);
  const val = (sap, k) => (draft[sap] && k in draft[sap] ? draft[sap][k] : (line(sap)?.[k] ?? (k === 'offered' ? false : null)));
  const dirty = Object.keys(draft).length + (po !== null ? 1 : 0);
  const pf = cust.price_field;
  const rows = useMemo(() => data.skus.filter((s) => skuMatch(s, q, lineup, cat) && (show === 'all' || val(s.sap_code, 'offered'))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.skus, q, lineup, cat, show, draft, data.lines]);
  const set = (sap, patch) => setDraft((d) => {
    const cur = { ...(d[sap] || {}), ...patch };
    if (patch.order_qty) cur.offered = true;
    if (patch.offered === false) cur.order_qty = null;
    const l = line(sap);
    const same = (!('offered' in cur) || !!cur.offered === !!(l?.offered)) && (!('order_qty' in cur) || (cur.order_qty || null) === (l?.order_qty || null));
    const next = { ...d };
    if (same) delete next[sap]; else next[sap] = cur;
    return next;
  });
  const tot = useMemo(() => {
    let ticked = 0; let qty = 0; let amount = 0;
    data.skus.forEach((s) => {
      if (val(s.sap_code, 'offered')) ticked += 1;
      const o = numOf(val(s.sap_code, 'order_qty'));
      if (o) { qty += o; amount += o * (s[pf] || 0); }
    });
    return { ticked, qty, amount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.skus, draft, data.lines, pf]);
  const run = async (fn, msg) => { setBusy(true); setErr(''); try { const r = await fn(); setData(r); if (msg) flash(msg(r)); return r; } catch (e) { setErr(e.message); return null; } finally { setBusy(false); } };
  const save = () => run(async () => {
    const lines = Object.entries(draft).map(([sap, v]) => ({ sap, ...v, ...(('order_qty' in v) ? { order_qty: v.order_qty === '' ? null : v.order_qty } : {}) }));
    const r = await call(`${API}/cycle/${data.cycle.cycle_pk}/sheet/${encodeURIComponent(cust.customer_key)}`, 'PUT', { lines, ...(po !== null ? { customer_po: po } : {}) });
    setDraft({}); setPo(null);
    return r;
  }, () => t('spl.savedCells', { n: dirty }));
  const status = (action) => run(() => call(`${API}/cycle/${data.cycle.cycle_pk}/sheet/${encodeURIComponent(cust.customer_key)}/status`, 'POST', { action }), () => t(`spl.st_${action}_done`, { cust: cust.customer_key }));
  const fromLast = () => run(() => call(`${API}/cycle/${data.cycle.cycle_pk}/sheet/${encodeURIComponent(cust.customer_key)}/ticks-from-last`, 'POST', {}),
    (r) => t('spl.fromLastDone', { n: r.ticked, date: fmtD(r.from) })).then((r) => { if (r) setShow('ticked'); });
  const tickShown = (on) => rows.forEach((s) => { if (!!val(s.sap_code, 'offered') !== on) set(s.sap_code, { offered: on }); });
  const close = () => { if (dirty) setLeaving(true); else onClose(); };
  const st = sheetState(sheet, mineLines);
  const why = !can.edit ? (data.cycle.status === 'closed' ? t('spl.closedNote') : !can.mine && !meta.can.manager ? t('spl.notYours', { pic: cust.pic_name || '—' })
    : st === 'done' || st === 'checked' ? t('spl.sheetLocked') : '') : '';
  const cols = [
    { key: 'tick', label: '✓', w: 40, render: (s) => <input type="checkbox" checked={!!val(s.sap_code, 'offered')} disabled={!can.edit} onChange={(e) => set(s.sap_code, { offered: e.target.checked })} aria-label={`${t('spl.tick')} ${s.sap_code}`} /> },
    { key: 'sap_code', label: t('spl.sap'), w: 92, cls: () => 'mono' },
    { key: 'product', label: t('spl.product'), w: 430, render: prodCell, title: (s) => `${s.product} · ${s.skin_color || ''}` },
    { key: 'line_category', label: t('spl.f_line_category'), w: 130 },
    { key: 'order_cycle', label: t('spl.f_order_cycle'), w: 160, render: (s) => (model.notThisCycle[s.sap_code] ? <span className="spl-chip warn" title={t('spl.flagCycle', { rule: s.order_cycle })}>{s.order_cycle}</span> : <span className="muted small">{s.order_cycle}</span>) },
    { key: 'price', label: t(`spl.pf_${pf}`), w: 90, num: true, render: (s) => (s[pf] == null ? <span className="spl-chip warn" title={t('spl.flagPrice')}>—</span> : m2(s[pf])) },
    { key: 'qty', label: t('spl.qty'), w: 100, num: true, cls: (s) => (draft[s.sap_code] ? 'ed typed' : 'ed'), render: (s) => (can.edit
      ? <input className="spl-qty" value={val(s.sap_code, 'order_qty') ?? ''} inputMode="numeric" onChange={(e) => set(s.sap_code, { order_qty: digits(e.target.value) ? Number(digits(e.target.value)) : null })} aria-label={`${s.sap_code} ${t('spl.qty')}`} />
      : n0(val(s.sap_code, 'order_qty'))) },
    { key: 'amount', label: t('spl.amountUsd'), w: 110, num: true, render: (s) => { const o = numOf(val(s.sap_code, 'order_qty')); return o && s[pf] != null ? m2(o * s[pf]) : ''; } },
  ];
  return (
    <Modal t={t} size="sheet" title={`${cust.customer_key} · ${t('spl.sheetTitle', { date: fmtD(data.cycle.order_due) })}`} onClose={close} error={err} testid="spl-sheet"
      foot={(
        <div className="spl-row">
          {can.done && <button type="button" className="pa-btn approve" onClick={() => status('done')} disabled={busy || !!dirty} title={dirty ? t('spl.saveFirst') : ''} data-testid="spl-sheet-done"><Check size={14} /> {t('spl.markDone')}</button>}
          {can.check && <button type="button" className="pa-btn approve" onClick={() => status('check')} disabled={busy || !!dirty} data-testid="spl-sheet-check"><Check size={14} /> {t('spl.markChecked')}</button>}
          {can.reopen && <button type="button" className="pa-btn" onClick={() => status('reopen')} disabled={busy || !!dirty} data-testid="spl-sheet-reopen"><RotateCcw size={14} /> {t('spl.reopen')}</button>}
          {can.edit && <button type="button" className="pa-btn submit" onClick={save} disabled={busy || !dirty} data-testid="spl-sheet-save">{busy ? <Loader2 size={14} className="pa-spin" /> : <Save size={14} />} {t('spl.saveN', { n: dirty })}</button>}
        </div>
      )}>
      <div className="spl-sheethead">
        <span className={`spl-chip st-${st}`} data-testid="spl-sheet-status">{t(`spl.sh_${st}`)}</span>
        <span>{t('spl.pic')}: <b>{cust.pic_name || '—'}</b></span>
        <span>{t('spl.priceCol')}: <b>{t(`spl.pf_${pf}`)}</b>{cust.entity ? <span className="muted"> · {cust.entity}</span> : null}</span>
        {sheet?.done_at && st !== 'open' && <span className="muted small">{t('spl.doneBy', { who: sheet.done_by, at: fmtDT(sheet.done_at) })}</span>}
        {sheet?.checked_at && <span className="muted small">{t('spl.checkedBy', { who: sheet.checked_by, at: fmtDT(sheet.checked_at) })}</span>}
        <span className="spl-grow" />
        <label className="spl-po">{t('spl.customerRef')}
          <input value={po ?? sheet?.customer_po ?? ''} onChange={(e) => setPo(e.target.value)} disabled={!can.edit} data-testid="spl-sheet-po" />
        </label>
      </div>
      {why && <div className="pa-banner ok">{why}</div>}
      {leaving && (
        <div className="pa-banner err spl-leave" data-testid="spl-sheet-leave">
          {t('spl.unsaved', { n: dirty })}
          <button type="button" className="pa-btn" onClick={() => setLeaving(false)}>{t('spl.keepEditing')}</button>
          <button type="button" className="pa-btn reject" onClick={onClose}>{t('spl.discard')}</button>
        </div>
      )}
      <div className="spl-toolbar">
        <div className="spl-seg" role="group">
          <button type="button" className={show === 'ticked' ? 'on' : ''} onClick={() => setShow('ticked')} data-testid="spl-show-ticked">{t('spl.showTicked', { n: tot.ticked })}</button>
          <button type="button" className={show === 'all' ? 'on' : ''} onClick={() => setShow('all')} data-testid="spl-show-all">{t('spl.showAll', { n: data.skus.length })}</button>
        </div>
        <SkuFilters t={t} model={model} q={q} setQ={setQ} lineup={lineup} setLineup={setLineup} cat={cat} setCat={setCat} testid="spl-sheet-search" />
        {can.edit && show === 'all' && <button type="button" className="pa-btn" onClick={() => tickShown(true)} data-testid="spl-tick-shown">{t('spl.tickShown')}</button>}
        {can.edit && <button type="button" className="pa-btn" onClick={() => tickShown(false)}>{t('spl.untickShown')}</button>}
        {can.edit && !anyTicked && <button type="button" className="pa-btn" onClick={fromLast} disabled={busy} data-testid="spl-from-last"><Copy size={14} /> {t('spl.fromLast')}</button>}
        <span className="spl-grow" />
        {tot.ticked && !dirty
          ? <a className="pa-btn" href={`${API}/cycle/${data.cycle.cycle_pk}/sheet/${encodeURIComponent(cust.customer_key)}/form.xlsx`} title={t('spl.exportTip')} data-testid="spl-sheet-export"><Download size={14} /> {t('spl.exportForm')}</a>
          : <button type="button" className="pa-btn" disabled title={dirty ? t('spl.saveFirst') : t('spl.tickFirst')} data-testid="spl-sheet-export"><Download size={14} /> {t('spl.exportForm')}</button>}
        {can.edit && <button type="button" className="pa-btn" onClick={() => setImporting(true)} disabled={!!dirty} title={dirty ? t('spl.saveFirst') : ''} data-testid="spl-sheet-import"><Upload size={14} /> {t('spl.importForm')}</button>}
      </div>
      {!anyTicked && !dirty && show === 'ticked' && <div className="spl-line">{t('spl.nothingTicked')}</div>}
      <VTable rows={rows} cols={cols} rowKey={(s) => s.sap_code} testid="spl-sheet-table" resetKey={`${show}|${q}|${lineup}|${cat}`} maxH="calc(100vh - 330px)"
        totals={{ tick: '', sap_code: t('spl.total'), qty: nz(tot.qty), amount: m2(tot.amount), product: t('spl.tickedN', { n: tot.ticked }) }} empty={show === 'ticked' ? t('spl.nothingTicked') : t('spl.noRows')} />
      {importing && <BuyerImport t={t} data={data} cust={cust} onClose={() => setImporting(false)} onDone={(r) => { setData(r); setImporting(false); flash(t('spl.formWritten', { cust: cust.customer_key, n: r.changes.length })); }} />}
    </Modal>
  );
}

function BuyerImport({ t, data, cust, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async (commit) => {
    setBusy(true); setErr('');
    try {
      const r = await call(`${API}/cycle/${data.cycle.cycle_pk}/sheet/${encodeURIComponent(cust.customer_key)}/import`, 'POST', { file: await readFile(file), fileName: file.name, commit });
      if (commit) onDone(r); else setPv(r);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="spl-overlay top2" role="presentation">
      <div className="spl-modal wide" role="dialog" aria-modal="true" data-testid="spl-form-dialog">
        <div className="spl-modal-head"><h3>{t('spl.formTitle', { cust: cust.customer_key })}</h3>
          <button type="button" className="spl-modal-x" onClick={onClose} aria-label={t('spl.close')}><X size={18} /></button></div>
        <div className="spl-modal-body">
          <p className="spl-p muted">{t('spl.formBody')}</p>
          <input type="file" accept=".xlsx,.xlsm" onChange={(e) => { setFile(e.target.files?.[0] || null); setPv(null); }} data-testid="spl-form-file" />
          {pv && (
            <div className="spl-preview" data-testid="spl-form-preview">
              <div className="spl-line">{t('spl.formSummary', { sheet: pv.sheet, rows: pv.rows, total: nz(pv.total), n: pv.changes.length })}</div>
              {pv.bad.length > 0 && <div className="pa-banner err">{pv.bad.slice(0, 8).join(' · ')}</div>}
              {pv.unknown.length > 0 && <div className="pa-banner err">{t('spl.formUnknown', { list: pv.unknown.slice(0, 12).join(', ') })}</div>}
              {pv.duplicates.length > 0 && <div className="pa-banner err">{t('spl.formDup', { list: pv.duplicates.slice(0, 12).join(', ') })}</div>}
              {pv.changes.length > 0 ? (
                <div className="spl-preview-box">
                  <table className="pa-table">
                    <thead><tr><th>{t('spl.sap')}</th><th>{t('spl.product')}</th><th className="num">{t('spl.was')}</th><th className="num">{t('spl.now')}</th><th /></tr></thead>
                    <tbody>{pv.changes.map((c) => (
                      <tr key={c.sap}><td className="mono">{c.sap}</td><td>{c.product}{c.color ? <span className="muted"> · {c.color}</span> : null}</td>
                        <td className="num">{nz(c.was)}</td><td className="num"><b>{nz(c.now)}</b></td>
                        <td>{!c.blocked.ok && <span className="spl-chip warn">{t('spl.flagCycle', { rule: c.blocked.why })}</span>}</td></tr>))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="spl-line">{t('spl.formNoChange')}</div>}
            </div>
          )}
        </div>
        {err && <div className="pa-banner err spl-modal-err">{err}</div>}
        <div className="spl-modal-foot">
          <span className="spl-grow" />
          <button type="button" className="pa-btn" onClick={onClose}>{t('spl.cancel')}</button>
          <button type="button" className="pa-btn submit" onClick={() => go(!!pv)} disabled={busy || !file || (pv && (!pv.changes.length || pv.bad.length))} data-testid="spl-form-save">
            {busy ? <Loader2 size={14} className="pa-spin" /> : null} {pv ? t('spl.writeN', { n: pv.changes.length }) : t('spl.read')}</button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================================================================== */
/* 3  STOCK                                                                                                            */
function StockTab({ t, meta, data, model, setData, flash }) {
  const [q, setQ] = useState('');
  const [lineup, setLineup] = useState('');
  const [cat, setCat] = useState('');
  const [active, setActive] = useState(true);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ask, setAsk] = useState(null);     // 'allocate' | 'import' | sku (allocation detail)
  const may = meta.can.manager && data.cycle.status !== 'closed';
  const pool = (s, k) => (draft[s.sap_code] && k in draft[s.sap_code] ? draft[s.sap_code][k] : s[k]);
  const rows = useMemo(() => data.skus.filter((s) => skuMatch(s, q, lineup, cat)
    && (!active || draft[s.sap_code] || (model.bySku.get(s.sap_code) || []).some((l) => l.order_qty || l.from_stock) || POOLS.some((k) => s[k]))),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [data.skus, q, lineup, cat, active, model]);
  const dirty = Object.keys(draft).length;
  const notDone = model.buyers.filter((c) => !['done', 'checked'].includes(model.sheets.get(c.customer_key)?.status)).length;
  const totals = useMemo(() => {
    const o = { order: 0, avail: 0, stock: 0, over: 0, kr: 0, eu: 0, ks: 0 };
    data.skus.forEach((s) => { const f = model.skuFig(s.sap_code); o.order += f.order; o.avail += f.avail; o.stock += f.stock; o.over += f.over; o.kr += s.stock_kr_dom || 0; o.eu += s.stock_eusu || 0; o.ks += s.stock_kr_sop || 0; });
    return o;
  }, [data.skus, model]);
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const r = await call(`${API}/cycle/${data.cycle.cycle_pk}/stock`, 'PUT', { rows: Object.entries(draft).map(([sap, v]) => ({ sap, ...v })) });
      setData(r); setDraft({}); flash(t('spl.savedCells', { n: dirty }));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const served = (sap) => (model.bySku.get(sap) || []).filter((l) => l.from_stock).map((l) => `${l.customer_key} ${nz(l.from_stock)}`).join(' · ');
  const cols = [
    { key: 'sap_code', label: t('spl.sap'), w: 92, cls: () => 'mono' },
    { key: 'product', label: t('spl.product'), w: 290, render: prodCell, title: (s) => `${s.product} · ${s.skin_color || ''}` },
    { key: 'order', label: t('spl.ordered'), w: 86, num: true, render: (s) => n0(model.skuFig(s.sap_code).order) },
    ...POOLS.map((k) => ({ key: k, label: t(`spl.pool_${k.replace('stock_', '')}`), w: 100, num: true, cls: (s) => (may ? `ed${draft[s.sap_code] && k in draft[s.sap_code] ? ' typed' : ''}` : ''),
      render: (s) => (may ? <input className="spl-qty" value={pool(s, k) ?? ''} inputMode="numeric" aria-label={`${s.sap_code} ${k}`}
        onChange={(e) => { const v = digits(e.target.value); setDraft((d) => ({ ...d, [s.sap_code]: { ...(d[s.sap_code] || {}), [k]: v ? Number(v) : null } })); }} /> : n0(s[k])) })),
    { key: 'avail', label: t('spl.avail'), w: 90, num: true, render: (s) => n0(model.skuFig(s.sap_code).avail) },
    { key: 'stock', label: t('spl.fromStock'), w: 92, num: true, cls: (s) => (model.skuFig(s.sap_code).over ? 'bad' : ''), render: (s) => n0(model.skuFig(s.sap_code).stock) },
    { key: 'left', label: t('spl.left'), w: 80, num: true, cls: (s) => (model.skuFig(s.sap_code).left < 0 ? 'bad' : ''), render: (s) => { const f = model.skuFig(s.sap_code); return f.avail || f.stock ? nz(f.left) : ''; } },
    { key: 'served', label: t('spl.allocation'), w: 300, render: (s) => (
      <span className="spl-served">
        {may && (model.bySku.get(s.sap_code) || []).length > 0 && <button type="button" className="spl-ibtn" title={t('spl.allocEdit', { sap: s.sap_code })} onClick={() => setAsk(s)} data-testid="spl-alloc-edit"><Pencil size={12} /></button>}
        <span className="small">{served(s.sap_code)}</span>
      </span>
    ), title: (s) => served(s.sap_code) },
  ];
  return (
    <div>
      <div className="spl-line">{t('spl.stockNote')}</div>
      {notDone > 0 && <div className="spl-warnline" data-testid="spl-not-done"><AlertTriangle size={12} /> {t('spl.notAllDone', { n: notDone })}</div>}
      {totals.over > 0 && <div className="spl-warnline red"><AlertTriangle size={12} /> {t('spl.countOver', { n: totals.over })}</div>}
      <div className="spl-toolbar">
        <SkuFilters t={t} model={model} q={q} setQ={setQ} lineup={lineup} setLineup={setLineup} cat={cat} setCat={setCat} testid="spl-stock-search" />
        <label className="spl-check"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('spl.onlyActive')}</label>
        <span className="spl-grow" />
        <a className="pa-btn" href={`${API}/cycle/${data.cycle.cycle_pk}/stock-template.xlsx`} data-testid="spl-stock-tpl"><Download size={14} /> {t('spl.stockTpl')}</a>
        {may && <button type="button" className="pa-btn" onClick={() => setAsk('import')} disabled={!!dirty} data-testid="spl-stock-import"><Upload size={14} /> {t('spl.stockImport')}</button>}
        {may && <button type="button" className="pa-btn" onClick={() => setAsk('allocate')} disabled={!!dirty} title={dirty ? t('spl.saveFirst') : ''} data-testid="spl-allocate"><Wand2 size={14} /> {t('spl.allocate')}</button>}
        {may && <button type="button" className="pa-btn submit" onClick={save} disabled={busy || !dirty} data-testid="spl-stock-save">{busy ? <Loader2 size={14} className="pa-spin" /> : <Save size={14} />} {t('spl.saveN', { n: dirty })}</button>}
      </div>
      {err && !ask && <div className="pa-banner err">{err}</div>}
      <VTable rows={rows} cols={cols} rowKey={(s) => s.sap_code} testid="spl-stock-table" resetKey={`${q}|${lineup}|${cat}|${active}`} empty={t('spl.noRows')}
        totals={{ sap_code: t('spl.total'), order: nz(totals.order), stock_kr_dom: nz(totals.kr), stock_eusu: nz(totals.eu), stock_kr_sop: nz(totals.ks), avail: nz(totals.avail), stock: nz(totals.stock), left: nz(totals.avail - totals.stock) }} />
      {ask === 'allocate' && <AllocateDialog t={t} data={data} notDone={notDone} onClose={() => setAsk(null)} onDone={(r) => { setData(r); setAsk(null); flash(t('spl.allocated', { skus: r.skus, pcs: nz(r.served) })); }} />}
      {ask === 'import' && <StockImport t={t} data={data} onClose={() => setAsk(null)} onDone={(r) => { setData(r); setAsk(null); flash(t('spl.stockWritten', { n: r.changes.length })); }} />}
      {ask && typeof ask === 'object' && <AllocationEdit t={t} data={data} model={model} sku={ask} onClose={() => setAsk(null)} onDone={(r) => { setData(r); setAsk(null); flash(t('spl.saved')); }} />}
    </div>
  );
}

function AllocateDialog({ t, data, notDone, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const pri = data.customers.filter((c) => c.alloc_priority != null).sort((a, b) => a.alloc_priority - b.alloc_priority).map((c) => c.customer_key);
  const go = async () => {
    setBusy(true); setErr('');
    try { onDone(await call(`${API}/cycle/${data.cycle.cycle_pk}/allocate`, 'POST', {})); } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <Modal t={t} title={t('spl.allocateTitle')} onClose={onClose} onSave={go} saveLabel={t('spl.allocateGo')} busy={busy} error={err} testid="spl-allocate-dialog">
      <p className="spl-p">{t('spl.allocateBody')}</p>
      {notDone > 0 && <p className="spl-warnline"><AlertTriangle size={12} /> {t('spl.notAllDone', { n: notDone })}</p>}
      <p className="spl-p muted">{t('spl.allocateOrder')}</p>
      <ol className="spl-pri">{pri.map((c) => <li key={c}>{c}</li>)}</ol>
    </Modal>
  );
}

function AllocationEdit({ t, data, model, sku, onClose, onDone }) {
  const ls = (model.bySku.get(sku.sap_code) || []).filter((l) => l.order_qty || l.from_stock);
  const [v, setV] = useState(Object.fromEntries(ls.map((l) => [l.customer_key, l.from_stock ?? ''])));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const avail = model.skuFig(sku.sap_code).avail;
  const served = Object.values(v).reduce((a, x) => a + numOf(x), 0);
  const go = async () => {
    setBusy(true); setErr('');
    try { onDone(await call(`${API}/cycle/${data.cycle.cycle_pk}/allocation/${encodeURIComponent(sku.sap_code)}`, 'PUT', { rows: Object.entries(v).map(([customer, f]) => ({ customer, from_stock: f === '' ? null : f })) })); } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <Modal t={t} title={t('spl.allocEdit', { sap: sku.sap_code })} onClose={onClose} onSave={go} busy={busy} error={err} testid="spl-alloc-dialog">
      <p className="spl-p">{sku.product}{sku.skin_color ? ` · ${sku.skin_color}` : ''}</p>
      <table className="pa-table">
        <thead><tr><th>{t('spl.buyer')}</th><th className="num">{t('spl.ordered')}</th><th className="num">{t('spl.fromStock')}</th></tr></thead>
        <tbody>{ls.map((l) => (
          <tr key={l.customer_key}><td>{l.customer_key}</td><td className="num">{nz(l.order_qty)}</td>
            <td className="num"><input className="spl-qty" value={v[l.customer_key] ?? ''} inputMode="numeric" onChange={(e) => setV((x) => ({ ...x, [l.customer_key]: digits(e.target.value) }))} aria-label={`${l.customer_key} ${t('spl.fromStock')}`} /></td></tr>))}
        </tbody>
      </table>
      <p className={`spl-line${served > avail ? ' bad' : ''}`}>{t('spl.allocTotal', { served: nz(served), avail: nz(avail) })}</p>
    </Modal>
  );
}

function StockImport({ t, data, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async (commit) => {
    setBusy(true); setErr('');
    try {
      const r = await call(`${API}/cycle/${data.cycle.cycle_pk}/stock/import`, 'POST', { file: await readFile(file), fileName: file.name, commit });
      if (commit) onDone(r); else setPv(r);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal t={t} size="wide" title={t('spl.stockImport')} onClose={onClose} busy={busy} error={err} testid="spl-stock-dialog"
      onSave={() => go(!!pv)} saveDisabled={!file || (pv && (!pv.changes.length || pv.bad.length))} saveLabel={pv ? t('spl.writeN', { n: pv.changes.length }) : t('spl.read')}>
      <p className="spl-p muted">{t('spl.stockImportBody')}</p>
      <input type="file" accept=".xlsx,.xlsm" onChange={(e) => { setFile(e.target.files?.[0] || null); setPv(null); }} data-testid="spl-stock-file" />
      {pv && (
        <div className="spl-preview" data-testid="spl-stock-preview">
          <div className="spl-line">{t('spl.stockSummary', { sheet: pv.sheet, rows: pv.rows, n: pv.changes.length })}</div>
          {pv.bad.length > 0 && <div className="pa-banner err">{pv.bad.slice(0, 8).join(' · ')}</div>}
          {pv.unknown.length > 0 && <div className="pa-banner err">{t('spl.formUnknown', { list: pv.unknown.slice(0, 12).join(', ') })}</div>}
          {pv.changes.length > 0 && (
            <div className="spl-preview-box">
              <table className="pa-table">
                <thead><tr><th>{t('spl.sap')}</th><th>{t('spl.product')}</th>{POOLS.map((k) => <th key={k} className="num">{t(`spl.pool_${k.replace('stock_', '')}`)}</th>)}</tr></thead>
                <tbody>{pv.changes.map((c) => (
                  <tr key={c.sap}><td className="mono">{c.sap}</td><td>{c.product}</td>
                    {c.now.map((x, i) => <td key={POOLS[i]} className="num">{(c.was[i] || null) !== (x || null) ? <><span className="muted">{nz(c.was[i])} → </span><b>{nz(x)}</b></> : nz(x)}</td>)}</tr>))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ================================================================================================================== */
/* 4  PRODUCTION                                                                                                       */
function ProductionTab({ t, meta, data, model, setData, flash }) {
  const [q, setQ] = useState('');
  const [lineup, setLineup] = useState('');
  const [cat, setCat] = useState('');
  const [needs, setNeeds] = useState(true);
  const [fac, setFac] = useState('');
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const may = (meta.can.factory || meta.can.manager) && data.cycle.status !== 'closed';
  const get = (s, k) => (draft[s.sap_code] && k in draft[s.sap_code] ? draft[s.sap_code][k] : s[k]);
  const put = (s, k, v) => setDraft((d) => ({ ...d, [s.sap_code]: { ...(d[s.sap_code] || {}), [k]: v } }));
  const facName = (code) => (data.factories.find((f) => f.code === code) || {}).name || code;
  const rows = useMemo(() => data.skus.filter((s) => {
    if (!skuMatch(s, q, lineup, cat)) return false;
    const f = model.skuFig(s.sap_code);
    if (needs && !draft[s.sap_code] && !(f.prod || s.overbuild)) return false;
    if (fac === '__none' && (draft[s.sap_code]?.factory ?? s.factory)) return false;
    if (fac && fac !== '__none' && (draft[s.sap_code]?.factory ?? s.factory) !== fac) return false;
    return true;
  }), [data.skus, q, lineup, cat, needs, fac, model, draft]);
  const cards = useMemo(() => {
    const by = {}; let none = 0; let total = 0;
    data.skus.forEach((s) => {
      const planned = model.skuFig(s.sap_code).prod + numOf(get(s, 'overbuild'));
      if (!planned) return;
      total += planned;
      const f = get(s, 'factory');
      if (f) by[f] = (by[f] || 0) + planned; else none += planned;
    });
    return { by, none, total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.skus, model, draft]);
  const dirty = Object.keys(draft).length;
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const r = await call(`${API}/cycle/${data.cycle.cycle_pk}/production`, 'PUT', { rows: Object.entries(draft).map(([sap, v]) => ({ sap, ...v })) });
      setData(r); setDraft({}); flash(t('spl.savedCells', { n: dirty }));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const buyersOf = (sap) => (model.bySku.get(sap) || []).map((l) => [l.customer_key, lineFig(l).prod]).filter(([, p]) => p).map(([k, p]) => `${k} ${nz(p)}`).join(' · ');
  const cols = [
    { key: 'sap_code', label: t('spl.sap'), w: 92, cls: () => 'mono' },
    { key: 'product', label: t('spl.product'), w: 290, render: prodCell, title: (s) => `${s.product} · ${s.skin_color || ''}` },
    { key: 'order', label: t('spl.ordered'), w: 84, num: true, render: (s) => n0(model.skuFig(s.sap_code).order) },
    { key: 'stock', label: t('spl.fromStock'), w: 88, num: true, render: (s) => n0(model.skuFig(s.sap_code).stock) },
    { key: 'prod', label: t('spl.required'), w: 92, num: true, render: (s) => <b>{n0(model.skuFig(s.sap_code).prod)}</b>, title: (s) => buyersOf(s.sap_code) },
    { key: 'overbuild', label: t('spl.overbuild'), w: 96, num: true, cls: (s) => (may ? `ed${draft[s.sap_code] && 'overbuild' in draft[s.sap_code] ? ' typed' : ''}` : ''),
      render: (s) => (may ? <input className="spl-qty" value={get(s, 'overbuild') ?? ''} inputMode="numeric" aria-label={`${s.sap_code} ${t('spl.overbuild')}`}
        onChange={(e) => { const v = digits(e.target.value); put(s, 'overbuild', v ? Number(v) : null); }} /> : n0(s.overbuild)) },
    { key: 'planned', label: t('spl.planned'), w: 92, num: true, render: (s) => <b>{n0(model.skuFig(s.sap_code).prod + numOf(get(s, 'overbuild')))}</b> },
    { key: 'factory', label: t('spl.factory'), w: 150, cls: (s) => (may ? `ed${draft[s.sap_code] && 'factory' in draft[s.sap_code] ? ' typed' : ''}` : ''),
      render: (s) => (may ? (
        <select className="spl-cellsel" value={get(s, 'factory') || ''} onChange={(e) => put(s, 'factory', e.target.value || null)} aria-label={`${s.sap_code} ${t('spl.factory')}`} data-testid="spl-factory">
          <option value="">{t('spl.unassigned')}</option>
          {data.factories.map((f) => <option key={f.code} value={f.code}>{f.name}</option>)}
        </select>
      ) : (s.factory ? facName(s.factory) : <span className="muted">{t('spl.unassigned')}</span>)) },
    { key: 'comment', label: t('spl.comment'), w: 220, cls: () => (may ? 'ed' : ''), render: (s) => (may
      ? <input className="spl-txt" value={get(s, 'comment') ?? ''} onChange={(e) => put(s, 'comment', e.target.value)} aria-label={`${s.sap_code} ${t('spl.comment')}`} />
      : <span className="small">{s.comment || ''}</span>), title: (s) => s.comment || '' },
    { key: 'buyers', label: t('spl.byBuyer'), w: 260, render: (s) => <span className="small muted">{buyersOf(s.sap_code)}</span>, title: (s) => buyersOf(s.sap_code) },
  ];
  return (
    <div>
      <div className="spl-line">{t('spl.prodNote')}</div>
      <div className="spl-cards" data-testid="spl-factory-cards">
        {data.factories.filter((f) => cards.by[f.code]).map((f) => (
          <button key={f.code} type="button" className={`spl-card btn${fac === f.code ? ' on' : ''}`} onClick={() => setFac(fac === f.code ? '' : f.code)}>
            <div className="k">{f.name}</div><div className="v">{nz(cards.by[f.code])}</div></button>))}
        <button type="button" className={`spl-card btn warn${fac === '__none' ? ' on' : ''}`} onClick={() => setFac(fac === '__none' ? '' : '__none')} data-testid="spl-card-unassigned">
          <div className="k">{t('spl.unassigned')}</div><div className="v">{nz(cards.none)}</div></button>
        <div className="spl-card"><div className="k">{t('spl.planned')}</div><div className="v">{nz(cards.total)}</div></div>
      </div>
      <div className="spl-toolbar">
        <SkuFilters t={t} model={model} q={q} setQ={setQ} lineup={lineup} setLineup={setLineup} cat={cat} setCat={setCat} testid="spl-prod-search" />
        <label className="spl-check"><input type="checkbox" checked={needs} onChange={(e) => setNeeds(e.target.checked)} /> {t('spl.needsOnly')}</label>
        <select value={fac} onChange={(e) => setFac(e.target.value)} aria-label={t('spl.factory')}>
          <option value="">{t('spl.allFactories')}</option>
          <option value="__none">{t('spl.unassigned')}</option>
          {data.factories.map((f) => <option key={f.code} value={f.code}>{f.name}</option>)}
        </select>
        <span className="spl-grow" />
        {may && <button type="button" className="pa-btn submit" onClick={save} disabled={busy || !dirty} data-testid="spl-prod-save">{busy ? <Loader2 size={14} className="pa-spin" /> : <Save size={14} />} {t('spl.saveN', { n: dirty })}</button>}
      </div>
      {err && <div className="pa-banner err">{err}</div>}
      <VTable rows={rows} cols={cols} rowKey={(s) => s.sap_code} testid="spl-prod-table" resetKey={`${q}|${lineup}|${cat}|${needs}|${fac}`} empty={t('spl.noRows')} />
    </div>
  );
}

/* ================================================================================================================== */
/* OVERVIEW — the tool's grid (SKU × buyer, three measures), read-only, and its customer report.                       */
function OverviewTab({ t, data, model }) {
  const [measure, setMeasure] = useState('order');
  const [q, setQ] = useState('');
  const [lineup, setLineup] = useState('');
  const [cat, setCat] = useState('');
  const [mode, setMode] = useState('amount');
  /* Only the buyers who ordered (or were served stock) this cycle get a column — 21 empty columns push the totals off
     the screen. The total sits right after the product, where it is always in view. */
  const buyers = useMemo(() => model.buyers.filter((c) => (model.byCust.get(c.customer_key) || []).some((l) => l.order_qty || l.from_stock)), [model]);
  const rows = useMemo(() => data.skus.filter((s) => skuMatch(s, q, lineup, cat) && (model.bySku.get(s.sap_code) || []).some((l) => l.order_qty || l.from_stock)),
    [data.skus, q, lineup, cat, model]);
  const fig = useMemo(() => {
    let order = 0; let stock = 0; let prod = 0; let amount = 0; let ob = 0;
    const byCatCust = {}; const byCust = {}; const byFrame = {};
    data.lines.forEach((l) => {
      const s = model.skuMap.get(l.sap_code); if (!s || !l.order_qty) return;
      const f = lineFig(l);
      const a = f.order * (model.priceOf(l.sap_code, l.customer_key) || 0);
      order += f.order; stock += Math.min(f.stock, f.order); prod += f.prod; amount += a;
      const c = s.product_category || '—';
      byCatCust[c] = byCatCust[c] || {};
      const x = byCatCust[c][l.customer_key] || { qty: 0, amount: 0 }; x.qty += f.order; x.amount += a; byCatCust[c][l.customer_key] = x;
      const bc = byCust[l.customer_key] || { qty: 0, amount: 0 }; bc.qty += f.order; bc.amount += a; byCust[l.customer_key] = bc;
      const fr = s.frame_category || '—'; byFrame[fr] = byFrame[fr] || { order: 0, prod: 0 }; byFrame[fr].order += f.order; byFrame[fr].prod += f.prod;
    });
    data.skus.forEach((s) => { ob += s.overbuild || 0; });
    return { order, stock, prod, amount, ob, byCatCust, byCust, byFrame };
  }, [data, model]);
  const per = (s, c) => { const f = lineFig(model.lines.get(`${s.sap_code}|${c.customer_key}`)); return f; };
  const W = { sap: 92, product: 260, c: 74, tot: 84 };
  const cols = [
    { key: 'sap_code', label: t('spl.sap'), w: W.sap, cls: () => 'mono' },
    { key: 'product', label: t('spl.product'), w: W.product, render: prodCell, title: (s) => `${s.product} · ${s.skin_color || ''}` },
    { key: 'total', label: t(`spl.total_${measure}`), w: W.tot, num: true, cls: () => 'strong', render: (s) => n0(model.skuFig(s.sap_code)[measure]) },
    ...buyers.map((c) => ({ key: c.customer_key, label: c.customer_key, w: W.c, num: true, tip: c.customer_key,
      cls: (s) => { const f = per(s, c); return f.over && measure !== 'order' ? 'bad' : (measure === 'order' && f.order && (model.notThisCycle[s.sap_code] || model.priceOf(s.sap_code, c.customer_key) == null) ? 'warnc' : ''); },
      render: (s) => n0(per(s, c)[measure]) })),
  ];
  const totals = { sap_code: t('spl.total'), total: nz(rows.reduce((a, s) => a + model.skuFig(s.sap_code)[measure], 0)) };
  buyers.forEach((c) => { totals[c.customer_key] = n0(rows.reduce((a, s) => a + per(s, c)[measure], 0)); });
  const custCols = buyers.filter((c) => fig.byCust[c.customer_key]);
  const cats = Object.keys(fig.byCatCust).sort((a, b) => Object.values(fig.byCatCust[b]).reduce((s, x) => s + x.amount, 0) - Object.values(fig.byCatCust[a]).reduce((s, x) => s + x.amount, 0));
  const cell = (x) => (mode === 'amount' ? (x?.amount ? m2(x.amount) : '') : n0(x?.qty));
  const card = (k, v, id) => <div className="spl-card" data-testid={id}><div className="k">{t(k)}</div><div className="v">{v}</div></div>;
  return (
    <div>
      <div className="spl-cards">
        {card('spl.c_orders', nz(fig.order), 'spl-c-orders')}
        {card('spl.c_stock', nz(fig.stock), 'spl-c-stock')}
        {card('spl.c_prod', nz(fig.prod), 'spl-c-prod')}
        {card('spl.c_ob', nz(fig.ob), 'spl-c-ob')}
        {card('spl.c_planned', nz(fig.prod + fig.ob), 'spl-c-planned')}
        {card('spl.c_amount', usd(fig.amount), 'spl-c-amount')}
      </div>
      <div className="spl-toolbar">
        <div className="spl-seg" role="group" data-testid="spl-measure">
          {['order', 'stock', 'prod'].map((m) => <button key={m} type="button" className={measure === m ? 'on' : ''} onClick={() => setMeasure(m)}>{t(`spl.m_${m}`)}</button>)}
        </div>
        <SkuFilters t={t} model={model} q={q} setQ={setQ} lineup={lineup} setLineup={setLineup} cat={cat} setCat={setCat} testid="spl-ov-search" />
        <span className="spl-line">{t(`spl.mNote_${measure}`)}</span>
      </div>
      <VTable rows={rows} cols={cols} rowKey={(s) => s.sap_code} testid="spl-grid" resetKey={`${q}|${lineup}|${cat}|${measure}`} totals={totals} empty={t('spl.noOrders')} maxH="60vh" frozen={3} />
      <section className="pa-section" style={{ marginTop: 14 }}>
        <div className="spl-toolbar">
          <h3 className="spl-h">{t('spl.customerReport')}</h3>
          <span className="spl-grow" />
          <div className="spl-seg" role="group">
            <button type="button" className={mode === 'amount' ? 'on' : ''} onClick={() => setMode('amount')}>{t('spl.amountUsd')}</button>
            <button type="button" className={mode === 'qty' ? 'on' : ''} onClick={() => setMode('qty')}>{t('spl.qty')}</button>
          </div>
        </div>
        <div className="spl-report-box">
          <table className="pa-table spl-report" data-testid="spl-report">
            <thead><tr><th>{t('spl.f_product_category')}</th>{custCols.map((c) => <th key={c.customer_key} className="num">{c.customer_key}</th>)}<th className="num">{t('spl.total')}</th></tr></thead>
            <tbody>
              {cats.map((c) => {
                const row = fig.byCatCust[c];
                const tt = Object.values(row).reduce((a, x) => ({ qty: a.qty + x.qty, amount: a.amount + x.amount }), { qty: 0, amount: 0 });
                return <tr key={c}><td>{c}</td>{custCols.map((x) => <td key={x.customer_key} className="num">{cell(row[x.customer_key])}</td>)}<td className="num"><b>{cell(tt)}</b></td></tr>;
              })}
              <tr className="spl-sum"><td>{t('spl.total')}</td>{custCols.map((x) => <td key={x.customer_key} className="num">{cell(fig.byCust[x.customer_key])}</td>)}
                <td className="num" data-testid="spl-report-total">{cell({ qty: fig.order, amount: fig.amount })}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="spl-line">{t('spl.priceRule')}</div>
      </section>
    </div>
  );
}

/* ================================================================================================================== */
/* SETUP — the buyers (with their PIC). The schedule is the orders table on Order Format now: an order is created there. */
const CUST_FIELDS = [['customer_key', 'text'], ['pic_user_id', 'sel'], ['country', 'text'], ['pricing', 'sel'], ['entity', 'text'], ['status', 'sel'],
  ['sold_to', 'text'], ['ship_to', 'text'], ['grid_order', 'text'], ['alloc_priority', 'text'], ['remark', 'text', 'wide']];

function SetupTab({ t, meta, reloadMeta, reloadCycle, flash }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const may = meta.can.manager;
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const { isNew, value, key } = edit;
      await call(isNew ? `${API}/customers` : `${API}/customers/${encodeURIComponent(key)}`, isNew ? 'POST' : 'PUT', value);
      await reloadMeta(); await reloadCycle();
      setEdit(null); flash(t('spl.saved'));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const open = (isNew, value, key) => { setErr(''); setEdit({ isNew, value: { ...value }, key }); };
  const custOpts = {
    pic_user_id: meta.people.map((p) => [String(p.id), p.name]),
    pricing: [['T0', 'T0'], ['T1', 'T1'], ['T2', 'T2']],
    status: [['active', t('spl.active')], ['discontinued', t('spl.discontinued')]],
  };
  return (
    <div>
      <section className="pa-section">
        <div className="spl-toolbar"><div className="spl-line">{t('spl.custNote')}</div><span className="spl-grow" />
          {may && <button type="button" className="pa-btn primary" onClick={() => open(true, { status: 'active', pricing: 'T1' })}><Plus size={14} /> {t('spl.addCustomer')}</button>}</div>
        <table className="pa-table" data-testid="spl-cust-table">
          <thead><tr>{['customer_key', 'pic_user_id', 'country', 'pricing', 'entity', 'status', 'sold_to', 'ship_to', 'grid_order', 'alloc_priority'].map((k) => <th key={k}>{t(`spl.f_${k}`)}</th>)}<th>{t('spl.priceCol')}</th><th /></tr></thead>
          <tbody>{meta.customers.map((c) => (
            <tr key={c.customer_key} className={c.status === 'discontinued' ? 'spl-dim' : ''}>
              <td><b>{c.customer_key}</b></td><td>{c.pic_name || <span className="muted">—</span>}</td><td>{c.country || ''}</td><td>{c.pricing || ''}</td><td>{c.entity || ''}</td>
              <td>{t(`spl.${c.status}`)}</td><td className="mono">{c.sold_to || ''}</td><td className="mono">{c.ship_to || ''}</td><td className="num">{c.grid_order ?? ''}</td><td className="num">{c.alloc_priority ?? ''}</td>
              <td className="small">{t(`spl.pf_${c.price_field}`)}</td>
              <td>{may && <button type="button" className="spl-ibtn" onClick={() => open(false, { ...c, pic_user_id: c.pic_user_id ? String(c.pic_user_id) : '' }, c.customer_key)} aria-label={`${t('spl.edit')} ${c.customer_key}`}><Pencil size={13} /></button>}</td>
            </tr>))}
          </tbody>
        </table>
      </section>
      {edit && (
        <Modal t={t} size="wide" title={t(`spl.edit_customer${edit.isNew ? '_new' : ''}`)} onClose={() => setEdit(null)} onSave={save} busy={busy} error={err} testid="spl-setup-dialog">
          <FieldGrid t={t} value={edit.value} onChange={(k, v) => setEdit((e) => ({ ...e, value: { ...e.value, [k]: v } }))}
            fields={CUST_FIELDS} locked={edit.isNew ? [] : ['customer_key']} options={custOpts} />
        </Modal>
      )}
    </div>
  );
}

/* ================================================================================================================== */
/* Which screens this reader may open — the same rule as the routes' grants, from what the server said the reader holds. */
const mayOpen = (can, key) => ({
  format: can.manager, stock: can.manager, setup: can.manager,
  orders: can.edit || can.manager, production: can.factory || can.manager, overview: true,
}[key]);

/* THE CYCLE PICKER (Max, 2026-10-20: one dropdown of every cycle "nếu làm 3 năm là sẽ rất nhiều"): the season first,
   then that season's cycles as a strip — at most a dozen, each saying its order and ex-factory dates. The next cycle
   whose order is due is marked; cycles gone by are muted. */
function CyclePicker({ t, meta, cyclePk, onPick }) {
  const cur = meta.cycles.find((c) => c.cycle_pk === cyclePk);
  const seasons = useMemo(() => [...new Set(meta.cycles.map((c) => c.season))], [meta.cycles]);
  const [season, setSeason] = useState(cur?.season || seasons[0]);
  useEffect(() => { if (cur) setSeason(cur.season); }, [cur]);
  const nextPk = (meta.cycles.find((c) => c.status === 'open' && c.order_due >= meta.today) || {}).cycle_pk;
  const list = meta.cycles.filter((c) => c.season === season);
  const pickSeason = (sn) => {
    setSeason(sn);
    const cs = meta.cycles.filter((c) => c.season === sn);
    const target = cs.find((c) => c.cycle_pk === nextPk) || cs.find((c) => c.order_due >= meta.today) || cs[cs.length - 1];
    if (target) onPick(target.cycle_pk);
  };
  return (
    <div className="spl-picker" data-testid="spl-picker">
      <div className="spl-seasons" role="group" aria-label={t('spl.season')}>
        {seasons.map((sn) => (
          <button key={sn} type="button" className={sn === season ? 'on' : ''} onClick={() => pickSeason(sn)} data-testid={`spl-season-${sn}`}>S{String(sn).slice(2)}</button>
        ))}
      </div>
      <div className="spl-cycles" role="group" aria-label={t('spl.cycle')}>
        {list.map((c) => {
          return (
            <button key={c.cycle_pk} type="button" data-testid="spl-cy" data-pk={c.cycle_pk} onClick={() => onPick(c.cycle_pk)}
              className={`spl-cy${c.cycle_pk === cyclePk ? ' on' : ''}${c.order_due < meta.today ? ' past' : ''}${c.status === 'closed' ? ' closed' : ''}`}
              title={`${c.order_no ? `${c.order_no} · ` : ''}${t('spl.f_order_due')} ${fmtD(c.order_due)} · ${t('spl.f_ex_factory')} ${fmtD(c.ex_factory)}${c.orders_total ? ` · ${t('xSpl.pcs', { n: nz(c.orders_total) })}` : ''}`}>
              <span className="d">{fmtD(c.order_due)}</span>
              <span className="x">{t('spl.exf')} {fmtD(c.ex_factory)}</span>
              {c.order_no ? <span className="tag">{c.order_no}</span> : null}
              {c.cycle_pk === nextPk ? <span className="next">{t('spl.next')}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one). One component serves all six Sales Planning screens (Order Format, Order
   Sheets, Stock, Production, Overview, Setup), and the old /sales-planning address lands on Order Sheets — so the steps
   speak of "this screen" and TourGuide skips the ones the screen has no control for (no order bar on Order Format or
   Setup). The tour opens by itself once, whichever screen the person meets first. */
const TOUR = [
  { selector: '[data-testid="spl-cyclebar"]', title: 'tourSalesPlanning.orderT', body: 'tourSalesPlanning.orderB' },
  { selector: '[data-testid="spl-steps"]', title: 'tourSalesPlanning.stepsT', body: 'tourSalesPlanning.stepsB' },
  { selector: '.spl-page .spl-toolbar', title: 'tourSalesPlanning.toolbarT', body: 'tourSalesPlanning.toolbarB' },
  { selector: '.spl-page .spl-vbox, .spl-page .spl-orders-wrap, [data-testid="spl-cust-table"], [data-testid="spl-report"]', title: 'tourSalesPlanning.tableT', body: 'tourSalesPlanning.tableB' },
];

export default function SalesPlanning({ screen }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const flash = useCallback((m) => { setMsg(m); window.setTimeout(() => setMsg((x) => (x === m ? '' : x)), 6000); }, []);
  const loadMeta = useCallback(() => call(`${API}/meta`).then((m) => { setMeta(m); return m; }).catch((e) => { setErr(e.message); return null; }), []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  /* The cycle on screen: the one in the URL, else the next open cycle whose order is due (that is where the work is). */
  const cyclePk = useMemo(() => {
    if (!meta) return null;
    const want = Number(params.get('cycle'));
    if (want && meta.cycles.some((c) => c.cycle_pk === want)) return want;
    /* Order Format is the list of orders: one opens only when it is asked for. */
    if (screen === 'format') return null;
    const next = meta.cycles.find((c) => c.status === 'open' && c.order_due >= meta.today);
    return (next || meta.cycles[meta.cycles.length - 1] || {}).cycle_pk || null;
  }, [meta, params, screen]);
  const loadCycle = useCallback(async () => {
    if (!cyclePk || screen === 'setup') return;
    try { setData(await call(`${API}/cycle/${cyclePk}`)); } catch (e) { setErr(e.message); }
  }, [cyclePk, screen]);
  useEffect(() => { setData(null); loadCycle(); }, [loadCycle]);
  const model = useModel(data);
  const setP = (patch) => { const n = new URLSearchParams(params); Object.entries(patch).forEach(([k, v]) => { if (v == null || v === '') n.delete(k); else n.set(k, v); }); setParams(n, { replace: true }); };
  const setDataAndMeta = (r) => { setData(r); loadMeta(); };
  const def = SPL_SCREENS.find((x) => x.key === screen) || SPL_SCREENS[1];

  /* Where the cycle stands, step by step — each a link to its screen when the reader may open it. */
  const steps = useMemo(() => {
    if (!model || !data) return [];
    const done = model.buyers.filter((c) => ['done', 'checked'].includes(model.sheets.get(c.customer_key)?.status)).length;
    let served = 0; let need = 0; let set = 0;
    data.lines.forEach((l) => { served += Math.min(l.from_stock || 0, l.order_qty || 0); });
    data.skus.forEach((s) => { const f = model.skuFig(s.sap_code); if (f.prod || s.overbuild) { need += 1; if (s.factory) set += 1; } });
    return [
      ['format', data.skus.length ? t('spl.fmtCount', { n: data.skus.length }) : t('spl.fmtNone')],
      ['orders', t('spl.sheetsCount', { done, n: model.buyers.length })],
      ['stock', served ? t('spl.stockServed', { n: nz(served) }) : '—'],
      ['production', need ? t('spl.prodCount', { set, n: need }) : '—'],
    ];
  }, [model, data, t]);
  const go = (key) => { const sc = SPL_SCREENS.find((x) => x.key === key); navigate(`${sc.path}${cyclePk ? `?cycle=${cyclePk}` : ''}`); };

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main spl-page">
        <div className="mh-titlerow">
          <div className="pa-title"><h2>{t(`nav.${def.nav}`)}</h2></div>
          <ModuleHelp steps={TOUR} tourKey="salesPlanning" ready={!!meta && (screen === 'setup' || screen === 'format' || !!data || !meta.cycles.length)} />
        </div>
        {err && <div className="pa-banner err">{err}</div>}
        {msg && <div className="pa-banner ok" data-testid="spl-msg">{msg}</div>}
        {meta && screen !== 'setup' && screen !== 'format' && meta.cycles.length > 0 && (
          <div className="spl-cyclebar" data-testid="spl-cyclebar">
            <CyclePicker t={t} meta={meta} cyclePk={cyclePk} onPick={(pk) => setP({ cycle: pk, sheet: null })} />
            {data && (
              <div className="spl-facts">
                {data.cycle.order_no && <span className="mono" data-testid="spl-order-no"><b>{data.cycle.order_no}</b></span>}
                <span><b>{t('spl.f_order_due')}</b> {fmtD(data.cycle.order_due)}</span>
                <span><b>{t('spl.f_ex_factory')}</b> {fmtD(data.cycle.ex_factory)}</span>
                <span className={`spl-chip ${data.cycle.status === 'closed' ? '' : 'ok'}`}>{t(`spl.st_${data.cycle.status}`)}</span>
                <span className="spl-grow" />
                <span className="spl-steps" data-testid="spl-steps">
                  {steps.map(([k, v], i) => {
                    const label = <><span className="spl-stepno">{i + 1}</span>{t(`nav.${SPL_SCREENS.find((x) => x.key === k).nav}`)} <span className="spl-badge">{v}</span></>;
                    return mayOpen(meta.can, k) && k !== screen
                      ? <button key={k} type="button" className="spl-step" onClick={() => go(k)}>{label}</button>
                      : <span key={k} className={`spl-step${k === screen ? ' on' : ' off'}`}>{label}</span>;
                  })}
                </span>
              </div>
            )}
          </div>
        )}
        {screen !== 'format' && data?.cycle.status === 'closed' && <div className="pa-banner ok">{t('spl.closedNote')}</div>}
        {meta && screen === 'format' && (
          <OrdersScreen t={t} meta={meta} reloadMeta={loadMeta} data={data} model={model} setData={setDataAndMeta} flash={flash}
            openPk={cyclePk} openMode={params.get('mode') === 'edit' ? 'edit' : 'view'}
            setOpen={(pk, mode) => setP({ cycle: pk, mode: pk && mode === 'edit' ? 'edit' : null })} />
        )}
        {meta && screen !== 'format' && screen !== 'setup' && !meta.cycles.length && (
          <section className="pa-section spl-emptybox" data-testid="spl-no-order"><p className="spl-p">{meta.can.manager ? t('spl.noOrderYetMgr') : t('spl.noOrderYet')}</p></section>
        )}
        {screen === 'format' || (meta && !meta.cycles.length && screen !== 'setup') ? null : !meta || (!data && screen !== 'setup') || (!model && screen !== 'setup') ? <div className="spl-line"><Loader2 size={14} className="pa-spin" /> {t('spl.loading')}</div> : (
          <>
            {screen === 'orders' && <SheetsTab t={t} meta={meta} data={data} model={model} setData={setDataAndMeta} flash={flash} openKey={params.get('sheet')} setOpenKey={(k) => setP({ sheet: k })} />}
            {screen === 'stock' && <StockTab t={t} meta={meta} data={data} model={model} setData={setDataAndMeta} flash={flash} />}
            {screen === 'production' && <ProductionTab t={t} meta={meta} data={data} model={model} setData={setDataAndMeta} flash={flash} />}
            {screen === 'overview' && <OverviewTab t={t} data={data} model={model} />}
            {screen === 'setup' && <SetupTab t={t} meta={meta} reloadMeta={loadMeta} reloadCycle={loadCycle} flash={flash} />}
          </>
        )}
      </div>
    </div>
  );
}

/* The old single address (/sales-planning) and its ?tab= keep working: they land on the screen the tab named. */
export function SalesPlanningLegacy() {
  const [params] = useSearchParams();
  const map = { format: 'format', sheets: 'orders', stock: 'stock', production: 'production', overview: 'overview', setup: 'setup' };
  const sc = SPL_SCREENS.find((x) => x.key === (map[params.get('tab')] || 'orders'));
  const n = new URLSearchParams(params); n.delete('tab');
  const q = n.toString();
  return <Navigate to={`${sc.path}${q ? `?${q}` : ''}`} replace />;
}
